import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import {
  Truck,
  Package,
  FileText,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Search,
  SlidersHorizontal,
  Download,
  MoreVertical,
  X,
  UploadCloud,
  File,
  ExternalLink,
  Phone,
  Plus,
  Check,
  ArrowRight,
  Eye,
  Printer,
  RotateCcw,
  Trash2,
} from 'lucide-react';
import { api, getApiErrorMessage } from '@/shared/api/axios';
import { formatINR } from '@/shared/utils/currency';
import { cn, localToday } from '@/shared/utils/utils';
import { Button } from '@/shared/components/ui/Button';
import { Badge } from '@/shared/components/ui/Badge';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { PageHeader } from '@/shared/components/ui/PageHeader';
import { StatCard, StatGrid } from '@/shared/components/ui/StatCard';
import { useUser } from '@/shared/hooks/useAuth';
import { canMarkDispatch, canSetTransportAmount, canReverseDispatch } from '@/shared/utils/dispatchAccess';
import { LocalTransportModal, localTransportToForm, announceVehicleShare, type LocalTransport } from '../components/LocalTransportModal';
import { ConfirmDialog } from '@/shared/components/ui/ConfirmDialog';
import type { DispatchRecord, DispatchKpis, DispatchStage } from '../types';
import { needsLocalTransportDetails, isCreditHandoff } from '../readiness';

const safeDate = (val: unknown, fallback = '—'): string => {
  if (!val) return fallback;
  try {
    const d = new Date(val as string | number);
    return isNaN(d.getTime()) ? fallback : d.toLocaleDateString('en-IN');
  } catch {
    return fallback;
  }
};

const safeTime = (val: unknown, fallback = ''): string => {
  if (!val) return fallback;
  try {
    const d = new Date(val as string | number);
    return isNaN(d.getTime()) ? fallback : d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return fallback;
  }
};

const safeMoney = (val: unknown, fallback = '0'): string => {
  if (val === null || val === undefined || val === '') return fallback;
  const num = Number(val);
  return isNaN(num) ? fallback : num.toLocaleString('en-IN');
};

const COURIER_PRESETS = [
  'Blue Dart Express',
  'DTDC Courier',
  'Delhivery Logistics',
  'Professional Courier',
  'Safechem Logistics (Chemical Cargo)',
  'Trackon Courier',
  'Shree Maruti Courier',
  'Direct Field Delivery/BDE Handover',
  'Other'
];

/** `readOnly` powers Sales' tracking view. Operations retains the work queue. */
export function DispatchAndChallansPage({ readOnly: salesView = false }: { readOnly?: boolean }) {
  const qc = useQueryClient();
  const user = useUser();
  // /sales/pending-dispatch is Sales' read-only tracking view. The Dispatch
  // team opening the same page works it like the work queue: Sales still only
  // sees status (canMarkDispatch is decided by department, never by role).
  const readOnly = salesView && !canMarkDispatch(user);
  // readOnly is the actual gate — it's hardcoded per route (true only on
  // /sales/pending-dispatch) — role/department alone must never re-open a
  // dispatch action on the read-only view, no matter what other workspace
  // access a viewer happens to hold.
  // Dispatch team only, by department — shared/utils/dispatchAccess.ts. Holding
  // the Inventory / Sales-Operations workspace (every BDM does) does NOT count.
  const canMarkDispatched = !readOnly && canMarkDispatch(user);

  // Search, filtering, and pagination state
  const [search, setSearch] = useState('');
  const [inStockOnly, setInStockOnly] = useState(false);
  // The Filters panel: narrow by transporter, client or destination.
  const [showFilters, setShowFilters] = useState(false);
  const [transporterFilter, setTransporterFilter] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [destinationFilter, setDestinationFilter] = useState('');
  const [selectedStage, setSelectedStage] = useState<string>('all');
  const [page, setPage] = useState(1);
  const pageSize = 10;

  // Selection state
  const [selectedRecordId, setSelectedRecordId] = useState<string | null>(null);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<'overview' | 'documents' | 'timeline'>('overview');

  // Modals and action state
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [actionMenuOpenId, setActionMenuOpenId] = useState<string | null>(null);

  // Side drawer document upload state
  const [docUploadKind, setDocUploadKind] = useState<'lr' | 'payment_proof' | 'pod'>('lr');
  const [docUploadNote, setDocUploadNote] = useState('');
  // Only meaningful for kind === 'payment_proof' — Sales is who actually
  // collects the money, so an amount entered here posts straight to the
  // invoice's real balance (same audited receipt path the hand-off form's
  // own payment arrangement already uses for the first payment).
  const [docPaymentAmount, setDocPaymentAmount] = useState('');
  const [docPaymentMethod, setDocPaymentMethod] = useState<'Cash' | 'UPI' | 'IMPS / NEFT'>('IMPS / NEFT');
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Query dispatch queue data from live backend
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['dispatch', 'queue', search, selectedStage],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search.trim()) params.set('search', search.trim());
      if (selectedStage !== 'all') params.set('dispatchStatus', selectedStage);
      const res = await api.get<{ data: DispatchRecord[]; kpis: DispatchKpis; total: number }>(
        `/api/sales-ops/orders/dispatch-queue${params.toString() ? `?${params.toString()}` : ''}`
      );
      return res.data;
    },
    refetchInterval: 30_000,
  });

  const records = data?.data || [];
  const kpis = data?.kpis || {
    awaitingDispatch: records.filter((r) => r.stage === 'awaiting_dispatch').length,
    draftChallans: records.filter((r) => r.stage === 'draft_challan').length,
    dispatchedToday: records.filter((r) => r.stage === 'dispatched' || r.stage === 'delivered').length,
    stockShort: records.filter((r) => r.stage === 'stock_short').length,
  };

  // Date filter — on the day the order was handed to Dispatch (else confirmed,
  // else raised). Presets fill From/To; editing either makes it a custom range.
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [datePreset, setDatePreset] = useState('');
  const [showProductWise, setShowProductWise] = useState(false);
  const applyPreset = (preset: string) => {
    setDatePreset(preset);
    const now = new Date();
    const shift = (days: number) => { const d = new Date(now); d.setDate(d.getDate() + days); return localToday(d); };
    const ranges: Record<string, [string, string]> = {
      today: [localToday(now), localToday(now)],
      yesterday: [shift(-1), shift(-1)],
      this_week: [shift(-((now.getDay() + 6) % 7)), localToday(now)],
      this_month: [localToday(new Date(now.getFullYear(), now.getMonth(), 1)), localToday(now)],
    };
    const r = ranges[preset];
    setFromDate(r ? r[0] : '');
    setToDate(r ? r[1] : '');
  };
  const recordDay = (r: DispatchRecord) => {
    const at = (r.stage === 'dispatched' || r.stage === 'delivered' ? (r.dispatchedAt || r.dispatchHandoff?.dispatchedAt) : null)
      || r.dispatchHandoff?.handedOffAt || r.confirmedAt || r.date;
    if (!at) return '';
    try {
      const d = new Date(at);
      return isNaN(d.getTime()) ? '' : localToday(d);
    } catch {
      return '';
    }
  };

  // Filter records in memory for "In stock only" and the date range
  const filteredRecords = useMemo(() => {
    let list = records;
    if (inStockOnly) {
      list = list.filter((r) => r.stockStatus === 'ready');
    }
    if (transporterFilter) list = list.filter((r) => (r.transportName || '—') === transporterFilter);
    if (clientFilter) list = list.filter((r) => (r.customerName || '—') === clientFilter);
    if (destinationFilter) list = list.filter((r) => (r.shippingDestination || '—') === destinationFilter);
    if (fromDate || toDate) {
      list = list.filter((r) => {
        const d = recordDay(r);
        return !!d && (!fromDate || d >= fromDate) && (!toDate || d <= toDate);
      });
    }
    return list;
  }, [records, inStockOnly, fromDate, toDate, transporterFilter, clientFilter, destinationFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const filterOptions = useMemo(() => {
    const uniq = (vals: string[]) => [...new Set(vals.map((v) => v || '—'))].sort((a, b) => a.localeCompare(b));
    return {
      transporters: uniq(records.map((r) => r.transportName)),
      clients: uniq(records.map((r) => r.customerName)),
      destinations: uniq(records.map((r) => r.shippingDestination)),
    };
  }, [records]);
  const activeExtraFilters = [transporterFilter, clientFilter, destinationFilter].filter(Boolean).length;

  // What the current view adds up to — the value of the orders listed, and
  // of those still to go out.
  const isPendingStage = (s: string) => s !== 'dispatched' && s !== 'delivered';
  const viewTotals = useMemo(() => {
    const pending = filteredRecords.filter((r) => isPendingStage(r.stage));
    return {
      count: filteredRecords.length,
      value: filteredRecords.reduce((n, r) => n + (Number(r.grandTotal) || 0), 0),
      pendingCount: pending.length,
      pendingValue: pending.reduce((n, r) => n + (Number(r.grandTotal) || 0), 0),
    };
  }, [filteredRecords]);

  // Product-wise pending dispatch — what still has to be produced/packed for
  // the orders in view that have not left yet (production planning).
  const productWise = useMemo(() => {
    const map = new Map<string, { product: string; pack: string; unit: string; quantity: number; orders: Set<string> }>();
    for (const r of filteredRecords) {
      if (!isPendingStage(r.stage)) continue;
      for (const l of r.lines || []) {
        const key = `${l.productId || l.description}|${l.packSize || ''}`;
        const row = map.get(key) || { product: l.description, pack: l.packSize || '', unit: l.unit || '', quantity: 0, orders: new Set<string>() };
        row.quantity += Number(l.quantity) || 0;
        row.orders.add(r.orderNumber);
        map.set(key, row);
      }
    }
    return [...map.values()].sort((a, b) => b.quantity - a.quantity);
  }, [filteredRecords]);

  // Product-wise pending — circulated to production / stores / management, so
  // it can be printed (Save as PDF from the print dialog) or exported to Excel.
  const unitLabel = (u: string) => (/\d/.test(u) ? u.split(' ').pop() || '' : u);
  const productWiseScope = () => {
    const bits: string[] = [];
    bits.push(fromDate || toDate ? `Dates: ${fromDate || '…'} to ${toDate || '…'}` : 'Dates: all');
    if (transporterFilter) bits.push(`Transporter: ${transporterFilter}`);
    if (clientFilter) bits.push(`Client: ${clientFilter}`);
    if (destinationFilter) bits.push(`Destination: ${destinationFilter}`);
    if (inStockOnly) bits.push('In stock only');
    if (search.trim()) bits.push(`Search: ${search.trim()}`);
    return bits.join(' · ');
  };
  const productWiseOrders = () => new Set(productWise.flatMap((p) => [...p.orders])).size;
  const exportProductWise = () => {
    const cell = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const lines: unknown[][] = [
      ['Product-wise pending dispatch — RBM Oil Corporation'],
      [productWiseScope()],
      [`Generated ${new Date().toLocaleString('en-IN')}`],
      [],
      ['#', 'Product', 'Pack', 'Quantity', 'Unit', 'Orders', 'Order numbers'],
      ...productWise.map((p, i) => [i + 1, p.product, p.pack, p.quantity, unitLabel(p.unit), p.orders.size, [...p.orders].join(', ')]),
      [],
      ['', `${productWise.length} products · ${productWiseOrders()} orders pending`],
    ];
    const csv = '\ufeff' + lines.map((l) => l.map(cell).join(',')).join('\r\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    a.download = `product-wise-pending-${new Date().toLocaleDateString('en-CA')}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };
  const printProductWise = () => {
    const esc = (v: unknown) => String(v ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' } as Record<string, string>)[c]);
    const rowsHtml = productWise.map((p, i) => `<tr><td>${i + 1}</td><td>${esc(p.product)}</td><td>${esc(p.pack || '—')}</td><td class="n">${p.quantity.toLocaleString('en-IN')} ${esc(unitLabel(p.unit))}</td><td class="n">${p.orders.size}</td><td class="o">${esc([...p.orders].join(', '))}</td></tr>`).join('');
    const w = window.open('', '_blank');
    if (!w) { toast.error('Allow pop-ups for this site to print.'); return; }
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Product-wise pending dispatch</title><style>
      body{font-family:Arial,Helvetica,sans-serif;color:#111;margin:24px}h1{font-size:18px;margin:0}h2{font-size:12px;font-weight:normal;color:#555;margin:4px 0 14px}
      table{width:100%;border-collapse:collapse;font-size:12px}th,td{border:1px solid #ccc;padding:6px 8px;text-align:left;vertical-align:top}th{background:#f1f5f9}
      td.n{text-align:right;white-space:nowrap}td.o{font-size:10px;color:#444}.foot{margin-top:10px;font-size:12px}@media print{body{margin:10mm}}
    </style></head><body>
      <h1>RBM Oil Corporation — Product-wise pending dispatch</h1>
      <h2>${esc(productWiseScope())} · Generated ${esc(new Date().toLocaleString('en-IN'))}</h2>
      <table><thead><tr><th>#</th><th>Product</th><th>Pack</th><th>Quantity</th><th>Orders</th><th>Order numbers</th></tr></thead><tbody>${rowsHtml}</tbody></table>
      <div class="foot"><b>${productWise.length}</b> products · <b>${productWiseOrders()}</b> orders pending</div>
    </body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 300);
  };

  const totalPages = Math.max(1, Math.ceil(filteredRecords.length / pageSize));
  const paginatedRecords = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredRecords.slice(start, start + pageSize);
  }, [filteredRecords, page, pageSize]);

  // Selected record for drawer
  const selectedRecord = useMemo(() => {
    if (!selectedRecordId) return null;
    return (
      records.find((r) => r._id === selectedRecordId || r.orderNumber === selectedRecordId) ||
      null
    );
  }, [records, selectedRecordId]);

  // Escape closes the record drawer.
  useEffect(() => {
    if (!selectedRecordId) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setSelectedRecordId(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedRecordId]);

  // Mutations for actions
  const createChallanMutation = useMutation({
    mutationFn: async (orderId: string) => {
      const res = await api.post(`/api/documents/transitions/sales_order/${orderId}/issue-delivery-challan`);
      return res.data;
    },
    onSuccess: () => {
      toast.success('Delivery Challan created and issued successfully!');
      qc.invalidateQueries({ queryKey: ['dispatch', 'queue'] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error || 'Failed to create delivery challan.');
    },
  });

  const markDispatchedMutation = useMutation({
    mutationFn: async ({ orderId, transporterName, vehicleNo, driverName, isSample, localTransport }: { orderId: string; transporterName?: string; vehicleNo?: string; driverName?: string, isSample?: boolean; localTransport?: LocalTransport }) => {
      if (isSample) {
        const res = await api.post(`/api/sampling/${orderId}/dispatch`, {
          dispatchMode: 'courier',
          courier: transporterName,
          vehicleNumber: vehicleNo,
          driverName,
        });
        return res.data;
      }
      const res = await api.post(`/api/sales-ops/orders/${orderId}/mark-dispatched`, {
        transporterName,
        vehicleNo,
        driverName,
        localTransport,
      });
      return res.data;
    },
    onSuccess: () => {
      setLocalTransportFor(null);
      toast.success('Consignment marked as dispatched! Stock movement posted.');
      qc.invalidateQueries({ queryKey: ['dispatch', 'queue'] });
    },
    onError: (err: any, vars) => {
      const code = err?.response?.data?.code;
      toast.error(err?.response?.data?.error || 'Failed to mark as dispatched.');
      // Local transport not filled yet — open it for Dispatch to fill in.
      if (code === 'LOCAL_TRANSPORT_REQUIRED' && localTransportFor) setFillTransportFor(localTransportFor);
      if (code) setLocalTransportFor(null);
      void vars;
    },
  });
  // Dispatch fills the local transport here when Mark Dispatched finds it missing.
  const [fillTransportFor, setFillTransportFor] = useState<DispatchRecord | null>(null);
  // Bulk selection of orders sent via the same vehicle / combined transport
  const [bulkTransportRecords, setBulkTransportRecords] = useState<DispatchRecord[] | null>(null);
  // A third-party order whose Mark Dispatched opened the transport form first.
  const [dispatchAfterSave, setDispatchAfterSave] = useState<DispatchRecord | null>(null);

  const saveTransport = useMutation({
    // The form sends exactly the fields of the record this order uses (local
    // transport OR the third-party transporter) — sent as they are.
    mutationFn: async ({ orderId, lt }: { orderId: string; lt: LocalTransport; isSample?: boolean }) =>
      (await api.put(`/api/sales-ops/orders/${orderId}/local-transport`, lt)).data,
    onSuccess: (data) => {
      setFillTransportFor(null);
      toast.success(canSetTransportAmount(user) ? 'Transport details saved.' : 'Transport details saved. The amount is entered later by an Admin.');
      announceVehicleShare(data);
      qc.invalidateQueries({ queryKey: ['dispatch'] });
      // Opened from Mark Dispatched — carry on to the dispatch confirmation.
      if (dispatchAfterSave) { setLocalTransportFor(dispatchAfterSave); setDispatchAfterSave(null); }
    },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Could not save transport details.'),
  });

  const saveBulkTransport = useMutation({
    mutationFn: async ({ orderIds, combinedAmount, lt }: { orderIds: string[]; combinedAmount?: string; lt: LocalTransport }) => {
      const payload = {
        orderIds,
        combinedAmount: combinedAmount || lt.amount || '',
        name: lt.name,
        mobile: lt.mobile,
        vehicleNo: lt.vehicleNo,
        payment: lt.payment,
        paymentMode: lt.paymentMode,
        trackingNo: lt.trackingNo,
        note: lt.note,
        freightPayment: lt.payment,
      };
      return (await api.put('/api/sales-ops/orders/bulk-local-transport', payload)).data;
    },
    onSuccess: (resData) => {
      setBulkTransportRecords(null);
      setSelectedRows(new Set());
      const perOrder = resData?.perOrderAmount !== null && resData?.perOrderAmount !== undefined ? ` (₹${resData.perOrderAmount} each)` : '';
      toast.success(canSetTransportAmount(user)
        ? `Combined transport saved for ${resData?.updatedOrdersCount || resData?.totalOrders} orders${perOrder}.`
        : `Combined transport details saved for ${resData?.updatedOrdersCount || resData?.totalOrders} orders. Amount will be entered by Admin.`
      );
      const skipped = (resData?.skipped || []) as Array<{ number: string; reason: string }>;
      if (skipped.length) toast(`Skipped ${skipped.length}: ${skipped.map((x) => `${x.number || 'order'} (${x.reason})`).join(', ')}`, { duration: 8000 });
      qc.invalidateQueries({ queryKey: ['dispatch'] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Could not save combined transport.'),
  });

  const canClearCredit = ['admin', 'superadmin', 'department_admin', 'manager', 'accounts', 'accountant'].includes(String(user?.role || '').toLowerCase())
    || ['accounts', 'management'].includes(String(user?.department || '').toLowerCase());

  const clearCreditDispatchMutation = useMutation({
    mutationFn: async ({ orderId, note }: { orderId: string; note?: string }) =>
      (await api.post(`/api/sales-ops/orders/${orderId}/clear-credit-dispatch`, { note })).data,
    onSuccess: () => {
      toast.success('Order cleared for dispatch under credit.');
      qc.invalidateQueries({ queryKey: ['dispatch'] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Failed to clear credit for dispatch.'),
  });

  const revokeCreditClearanceMutation = useMutation({
    mutationFn: async (orderId: string) =>
      (await api.delete(`/api/sales-ops/orders/${orderId}/clear-credit-dispatch`)).data,
    onSuccess: () => {
      toast.success('Credit clearance revoked.');
      qc.invalidateQueries({ queryKey: ['dispatch'] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Failed to revoke credit clearance.'),
  });

  // An order asks for a confirmation (the local transport was recorded by
  // Sales at hand-off); a sample goes straight through as before.
  const [localTransportFor, setLocalTransportFor] = useState<DispatchRecord | null>(null);
  // Mark Dispatched (client, 2026-10-07) — rules in ../readiness.ts, the same
  // as the server's. Local transport details only when the order uses local
  // transport; no POD needed to dispatch (it is needed to mark delivered).
  const startDispatch = (r: DispatchRecord) => {
    if (r.documentType === 'sample_request') { markDispatchedMutation.mutate({ orderId: r._id, isSample: true }); return; }
    if (needsLocalTransportDetails(r.dispatchHandoff as Parameters<typeof needsLocalTransportDetails>[0])) {
      toast('Fill in the local transport first — transporter name, mobile and vehicle number.');
      setFillTransportFor(r);
      return;
    }
    // Third-party transporter (client, 2026-10-07): offer its details form
    // first when nothing is recorded yet — optional, Save and it dispatches.
    const tp = (r.dispatchHandoff as { thirdPartyTransport?: { vehicleNo?: string; mobile?: string } | null } | null)?.thirdPartyTransport;
    const hasCourier = Boolean(r.transportName && r.transportName !== '—') || Boolean(r.dispatchHandoff?.courierName);
    if (r.transportMode === 'third_party_courier' && !hasCourier && !tp?.vehicleNo && !tp?.mobile) {
      setDispatchAfterSave(r);
      setFillTransportFor(r);
      return;
    }
    // The amount is NOT needed to dispatch (client, 2026-10-06): Superadmin /
    // Admin / Department Administrator enter it afterwards and are notified.
    setLocalTransportFor(r);
  };

  const markDeliveredMutation = useMutation({
    mutationFn: async ({ orderId, isSample }: { orderId: string, isSample?: boolean }) => {
      if (isSample) {
        const res = await api.post(`/api/sampling/${orderId}/delivered`);
        return res.data;
      }
      const res = await api.post(`/api/sales-ops/orders/${orderId}/mark-delivered`);
      return res.data;
    },
    onSuccess: () => {
      toast.success('Delivery confirmed and marked as delivered!');
      qc.invalidateQueries({ queryKey: ['dispatch', 'queue'] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error || 'Failed to mark as delivered.');
    },
  });

  const reverseDispatchMutation = useMutation({
    mutationFn: async ({ orderId, isSample }: { orderId: string; isSample?: boolean }) => {
      const url = isSample ? `/api/sampling/${orderId}/reverse-dispatch` : `/api/sales-ops/orders/${orderId}/reverse-dispatch`;
      const res = await api.post(url);
      return res.data;
    },
    onSuccess: () => {
      toast.success('Dispatch reversed successfully! Inventory stock movement restored.');
      qc.invalidateQueries({ queryKey: ['dispatch'] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error || 'Failed to reverse dispatch.');
    },
  });

  const requestReversalMutation = useMutation({
    mutationFn: async ({ orderId, reason }: { orderId: string; reason: string }) => {
      const res = await api.post(`/api/sales-ops/orders/${orderId}/request-dispatch-reversal`, { reason });
      return res.data;
    },
    onSuccess: () => {
      toast.success('Dispatch reversal request submitted. Waiting for Admin approval.');
      qc.invalidateQueries({ queryKey: ['dispatch'] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error || 'Failed to request dispatch reversal.');
    },
  });

  const rejectReversalMutation = useMutation({
    mutationFn: async ({ orderId, reason }: { orderId: string; reason?: string }) => {
      const res = await api.post(`/api/sales-ops/orders/${orderId}/reject-dispatch-reversal`, { reason });
      return res.data;
    },
    onSuccess: () => {
      toast.success('Reversal request rejected.');
      qc.invalidateQueries({ queryKey: ['dispatch'] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error || 'Failed to reject reversal request.');
    },
  });

  const deleteDocMutation = useMutation({
    mutationFn: async ({ orderId, url, docId, name, kind }: { orderId: string; url?: string; docId?: string; name?: string; kind?: string }) => {
      const res = await api.delete(`/api/sales-ops/orders/${orderId}/dispatch-document`, {
        data: { url, docId, name, kind },
      });
      return res.data;
    },
    onSuccess: () => {
      toast.success('Document deleted. You can now upload a replacement.');
      qc.invalidateQueries({ queryKey: ['dispatch'] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error || 'Failed to delete document.');
    },
  });

  // Handle file upload
  const handleFileUpload = async (files: FileList | null) => {
    if (!files || files.length === 0 || !selectedRecord) return;
    setIsUploading(true);
    try {
      const fileList = Array.from(files);
      let amountPosted = false;
      for (const file of fileList) {
        if (file.size > 25 * 1024 * 1024) {
          toast.error(`"${file.name}" exceeds the 25 MB limit.`);
          continue;
        }
        const formData = new FormData();
        formData.append('file', file);
        const uploadRes = await api.post<{ url: string }>('/api/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        if (uploadRes.data?.url) {
          if (selectedRecord.documentType === 'sample_request') {
             // For samples, maybe we can attach it to the sample directly, but there's no endpoint in the sample API for document attachment.
             // Currently skipping docs for samples to avoid breaking, or we could add it later.
          } else {
            // Amount only goes on the FIRST file of a multi-file batch — it's
            // one payment, not one per file, and posting it on every file
            // would record the same receipt that many times.
            const includeAmount = docUploadKind === 'payment_proof' && !amountPosted && docPaymentAmount.trim();
            const { data } = await api.post<{ payment?: { amountDue: number } }>(
              `/api/sales-ops/orders/${selectedRecord._id}/dispatch-document`,
              {
                kind: docUploadKind,
                url: uploadRes.data.url,
                name: file.name,
                size: file.size,
                note: docUploadNote.trim() || undefined,
                ...(includeAmount ? { amount: Number(docPaymentAmount), paymentArrangement: docPaymentMethod } : {}),
              },
            );
            if (includeAmount) {
              amountPosted = true;
              if (data?.payment) {
                toast.success(`Payment recorded. Balance due now ₹${safeMoney(data.payment.amountDue)}.`, { duration: 6000 });
              }
            }
          }
        }
      }
      toast.success('Document uploaded and attached successfully!');
      qc.invalidateQueries({ queryKey: ['dispatch', 'queue'] });
      setDocUploadNote('');
      setDocPaymentAmount('');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to upload document.');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Cash has no bank/UPI receipt to attach, so recording it doesn't go
  // through the file drop-zone at all — same server route, just no url.
  const handleRecordCashPayment = async () => {
    if (!selectedRecord || !docPaymentAmount.trim()) return;
    setIsUploading(true);
    try {
      const { data } = await api.post<{ payment?: { amountDue: number } }>(
        `/api/sales-ops/orders/${selectedRecord._id}/dispatch-document`,
        {
          kind: 'payment_proof',
          note: docUploadNote.trim() || undefined,
          amount: Number(docPaymentAmount),
          paymentArrangement: 'Cash',
        },
      );
      if (data?.payment) {
        toast.success(`Payment recorded. Balance due now ₹${safeMoney(data.payment.amountDue)}.`, { duration: 6000 });
      }
      qc.invalidateQueries({ queryKey: ['dispatch', 'queue'] });
      setDocUploadNote('');
      setDocPaymentAmount('');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to record payment.');
    } finally {
      setIsUploading(false);
    }
  };

  // Export records to CSV
  const handleExport = () => {
    if (!records.length) return;
    const headers = ['Order No', 'Date', 'Due Date', 'Dispatched Date', 'Client', 'Phone', 'Destination', 'Handoff / Transport', 'Stage', 'DC Number', 'Docs Count'];
    const rows = records.map((r) => [
      r.orderNumber,
      safeDate(r.confirmedAt || r.date),
      safeDate(r.requiredDispatchDate || r.requiredDeliveryDate, '-'),
      safeDate(r.dispatchedAt || r.dispatchHandoff?.dispatchedAt, '-'),
      r.customerName,
      r.customerPhone,
      r.shippingDestination,
      r.transportName + (r.vehicleNo ? ` (${r.vehicleNo})` : ''),
      stageLabel(r.stage),
      r.dcNumber || r.dcDraftNumber || '—',
      `${r.docsCount}/3`,
    ]);
    const csvContent = 'data:text/csv;charset=utf-8,' + [headers, ...rows].map((e) => e.map((val) => `"${String(val).replace(/"/g, '""')}"`).join(',')).join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `dispatch_and_challans_${localToday()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Toggle row selection
  const toggleSelectRow = (id: string) => {
    const next = new Set(selectedRows);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedRows(next);
  };

  const toggleSelectAll = () => {
    if (selectedRows.size === paginatedRecords.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(paginatedRecords.map((r) => r._id)));
    }
  };

  return (
    <div className="mx-auto min-h-screen max-w-[1580px] space-y-5 pb-16 text-slate-800">
      {fillTransportFor && (
        <LocalTransportModal
          mode="edit"
          orderNumber={fillTransportFor.orderNumber}
          sentVia={fillTransportFor.transportName && fillTransportFor.transportName !== '—' ? fillTransportFor.transportName : undefined}
          thirdParty={fillTransportFor.transportMode !== 'local_transport'}
          twoLegs={fillTransportFor.transportMode === 'third_party_courier' && fillTransportFor.documentType !== 'sample_request'}
          localOnly={fillTransportFor.transportMode === 'local_transport' || (fillTransportFor.dispatchHandoff as { localTransportUsed?: boolean | null } | null)?.localTransportUsed === true}
          initial={localTransportToForm((fillTransportFor.dispatchHandoff as { localTransport?: Record<string, unknown> } | null)?.localTransport || null, fillTransportFor.dispatchHandoff as unknown as Record<string, unknown>)}
          busy={saveTransport.isPending}
          onCancel={() => { setFillTransportFor(null); setDispatchAfterSave(null); }}
          onConfirm={(lt) => saveTransport.mutate({ orderId: fillTransportFor._id, lt, isSample: fillTransportFor.documentType === 'sample_request' })}
        />
      )}
      {bulkTransportRecords && bulkTransportRecords.length > 0 && (
        <LocalTransportModal
          mode="edit"
          bulkRecords={bulkTransportRecords}
          busy={saveBulkTransport.isPending}
          onCancel={() => setBulkTransportRecords(null)}
          onConfirm={(lt, bulkIds) => saveBulkTransport.mutate({
            orderIds: bulkIds || bulkTransportRecords.map((r) => r._id),
            combinedAmount: lt.amount,
            lt,
          })}
        />
      )}
      {/* Mark Dispatched: the server checks the local transport (details by
          Dispatch, amount by Admin / Superadmin) and says what is missing. */}
      <ConfirmDialog
        open={!!localTransportFor}
        title={`Mark ${localTransportFor?.orderNumber || 'order'} as dispatched?`}
        message="The goods have left the plant. Stock is moved out and the sales person is told."
        confirmLabel="Mark as Dispatched"
        loading={markDispatchedMutation.isPending}
        onConfirm={() => localTransportFor && markDispatchedMutation.mutate({ orderId: localTransportFor._id })}
        onCancel={() => setLocalTransportFor(null)}
      />
      <PageHeader
        icon={Truck}
        eyebrow="Dispatch"
        title={readOnly ? 'Dispatch Status' : 'Dispatch & Challans'}
        description={readOnly
          ? 'Track whether your orders are awaiting dispatch, dispatched or delivered. The Dispatch team handles fulfilment.'
          : 'Hand-offs from Sales, challans, dispatch progress and delivery records in one place.'}
        actions={!readOnly && (
          <Button onClick={() => setIsCreateModalOpen(true)}>
            <Plus className="h-4 w-4" /> Create Dispatch Order
          </Button>
        )}
      />

      <StatGrid cols={4}>
        <StatCard label="Awaiting Dispatch" value={kpis.awaitingDispatch} hint="Confirmed, not yet fulfilled" icon={Truck} tone="primary" />
        <StatCard label="Draft Challans" value={kpis.draftChallans} hint="Challan created, pending dispatch" icon={FileText} tone="info" />
        <StatCard label="Dispatched Today" value={kpis.dispatchedToday} hint="Left the warehouse today" icon={CheckCircle2} tone="success" />
        <StatCard label="Stock Short" value={kpis.stockShort} hint="Awaiting BDM response" icon={AlertCircle} tone="danger" />
      </StatGrid>

      {/* ── Main Work Area: Table + Side Drawer ── */}
      <div className="flex flex-col lg:flex-row items-start gap-6">
        {/* Table Container */}
        <div className="w-full min-w-0 flex-1 overflow-hidden rounded-card border border-border bg-card shadow-xs">
          {/* Card Top Title & Export */}
          <div className="px-4 py-3 border-b border-border flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-headline-sm font-semibold text-on-surface">
              All Dispatch Records <span className="font-mono tabular-nums text-body-sm font-normal text-on-surface-subtle">({filteredRecords.length})</span>
            </h2>
            <button
              onClick={handleExport}
              className="inline-flex items-center gap-1.5 rounded-lg border border-input bg-surface px-3 py-1.5 text-body-sm font-medium text-on-surface transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Download className="h-3.5 w-3.5 text-on-surface-variant" />
              <span>Export</span>
            </button>
          </div>

          {/* Search & Filter Toolbar */}
          <div className="p-3 border-b border-border bg-surface-variant/60 flex flex-wrap items-center gap-2 justify-between">
            <div className="relative flex-1 min-w-[260px] max-w-md">
              <label htmlFor="dispatch-search" className="sr-only">Search dispatch records</label>
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-on-surface-subtle" aria-hidden />
              <input
                id="dispatch-search"
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Order no., client, destination or DC number"
                className="w-full pl-9 pr-4 py-2 text-body-sm rounded-lg border border-input bg-surface text-on-surface focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:border-primary transition placeholder:text-on-surface-subtle"
              />
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <label className="flex items-center gap-2 cursor-pointer select-none text-body-sm font-medium text-on-surface-variant">
                <input
                  type="checkbox"
                  checked={inStockOnly}
                  onChange={(e) => setInStockOnly(e.target.checked)}
                  className="rounded border-outline-variant text-primary focus:ring-ring h-4 w-4 accent-primary"
                />
                <span>In stock only</span>
              </label>

              <label htmlFor="dispatch-stage-filter" className="sr-only">Stage</label>
              <select
                id="dispatch-stage-filter"
                value={selectedStage}
                onChange={(e) => setSelectedStage(e.target.value)}
                className="text-body-sm font-medium text-on-surface bg-surface border border-input rounded-lg px-3 py-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value="all">All stages</option>
                <option value="awaiting_dispatch">Awaiting Dispatch</option>
                {/* Pending Dispatch keeps only these three stages (client request),
                    whoever opens it — Dispatch & Challans has the full list. */}
                {!salesView && <option value="draft_challan">Draft Challan</option>}
                <option value="dispatched">Dispatched</option>
                {!salesView && <option value="delivered">Delivered</option>}
                {!salesView && <option value="stock_short">Stock Short</option>}
                <option value="overdue">Overdue Dispatches</option>
              </select>

              <select
                aria-label="Hand-off date"
                value={datePreset}
                onChange={(e) => applyPreset(e.target.value)}
                className="text-body-sm font-medium text-on-surface bg-surface border border-input rounded-lg px-3 py-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value="">All dates</option>
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="this_week">This week</option>
                <option value="this_month">This month</option>
                {datePreset === 'custom' && <option value="custom">Custom range</option>}
              </select>
              <input type="date" aria-label="From date" value={fromDate} max={toDate || undefined}
                onChange={(e) => { setFromDate(e.target.value); setDatePreset('custom'); }}
                className="text-body-sm text-on-surface bg-surface border border-input rounded-lg px-2 py-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
              <span className="text-body-sm text-on-surface-subtle">to</span>
              <input type="date" aria-label="To date" value={toDate} min={fromDate || undefined}
                onChange={(e) => { setToDate(e.target.value); setDatePreset('custom'); }}
                className="text-body-sm text-on-surface bg-surface border border-input rounded-lg px-2 py-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" />

              <button
                type="button"
                onClick={() => setShowProductWise((v) => !v)}
                aria-pressed={showProductWise}
                className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border text-body-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${showProductWise ? 'border-primary/30 bg-primary-container text-on-primary-container' : 'border-input bg-surface text-on-surface hover:bg-muted'}`}
              >
                <Package className="h-3.5 w-3.5" aria-hidden />
                <span>Product-wise pending</span>
              </button>

              <button
                type="button"
                onClick={() => setShowFilters((v) => !v)}
                aria-pressed={showFilters}
                className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border text-body-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${showFilters || activeExtraFilters ? 'border-primary/30 bg-primary-container text-on-primary-container' : 'border-input bg-surface text-on-surface hover:bg-muted'}`}
              >
                <SlidersHorizontal className="h-3.5 w-3.5" aria-hidden />
                <span>Filters{activeExtraFilters ? ` (${activeExtraFilters})` : ''}</span>
              </button>
            </div>
          </div>

          {showFilters && (
            <div className="flex flex-wrap items-end gap-2 border-b border-border bg-surface-variant px-4 py-3">
              {([
                ['Transporter', transporterFilter, setTransporterFilter, filterOptions.transporters],
                ['Client', clientFilter, setClientFilter, filterOptions.clients],
                ['Destination', destinationFilter, setDestinationFilter, filterOptions.destinations],
              ] as const).map(([label, value, setValue, options]) => (
                <label key={label} className="flex min-w-[200px] flex-col gap-1 text-label-sm font-semibold text-on-surface-variant">
                  {label}
                  <select value={value} onChange={(e) => { setValue(e.target.value); setPage(1); }}
                    className="rounded-lg border border-input bg-surface px-2.5 py-2 text-body-sm font-medium text-on-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                    <option value="">All</option>
                    {options.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </label>
              ))}
              <button type="button"
                onClick={() => { setSearch(''); setInStockOnly(false); setSelectedStage('all'); applyPreset(''); setTransporterFilter(''); setClientFilter(''); setDestinationFilter(''); setPage(1); }}
                className="rounded-lg border border-input bg-surface px-3 py-2 text-body-sm font-medium text-on-surface transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                Clear all filters
              </button>
            </div>
          )}

          {/* What the view adds up to — follows every filter above. */}
          <div className="flex flex-wrap items-center gap-x-6 gap-y-1 border-b border-border bg-surface-variant px-4 py-2.5 text-body-sm text-on-surface-variant">
            <span><b className="font-mono tabular-nums font-semibold text-on-surface">{viewTotals.count}</b> order(s) · value <b className="font-mono tabular-nums font-semibold text-on-surface">{formatINR(viewTotals.value)}</b></span>
            <span>Pending dispatch: <b className="font-mono tabular-nums font-semibold text-warning-text">{viewTotals.pendingCount}</b> · <b className="font-mono tabular-nums font-semibold text-warning-text">{formatINR(viewTotals.pendingValue)}</b></span>
            {(fromDate || toDate) && (
              <span className="text-on-surface-subtle">Handed to Dispatch {fromDate === toDate ? `on ${fromDate}` : `${fromDate || 'start'} → ${toDate || 'today'}`}</span>
            )}
          </div>

          {showProductWise && (
            <div className="border-b border-border px-4 py-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-headline-sm font-semibold text-on-surface">Product-wise pending dispatch</h3>
                {productWise.length > 0 && (
                  <div className="flex gap-2">
                    <button type="button" onClick={printProductWise} className="inline-flex items-center gap-1.5 rounded-lg border border-input bg-surface px-2.5 py-1.5 text-body-sm font-medium text-on-surface transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                      <Printer className="h-3.5 w-3.5" aria-hidden /> Print / PDF
                    </button>
                    <button type="button" onClick={exportProductWise} className="inline-flex items-center gap-1.5 rounded-lg border border-input bg-surface px-2.5 py-1.5 text-body-sm font-medium text-on-surface transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                      <Download className="h-3.5 w-3.5" aria-hidden /> Export (Excel)
                    </button>
                  </div>
                )}
              </div>
              <p className="mb-2 text-body-sm text-on-surface-variant">What still has to go out for the orders in view (not yet dispatched) — for planning the day&apos;s production.</p>
              {productWise.length === 0 ? (
                <p className="text-body-sm text-on-surface-variant">Nothing pending under these filters — every line in view has already gone out.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[560px] text-left text-body-md">
                    <thead>
                      <tr className="border-b border-border bg-muted text-label-sm font-semibold uppercase tracking-wider text-on-surface-variant">
                        <th scope="col" className="py-2 pr-3">Product</th><th scope="col" className="py-2 pr-3">Pack</th><th scope="col" className="py-2 pr-3 text-right">Quantity</th><th scope="col" className="py-2 text-right">Orders</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {productWise.map((p) => (
                        <tr key={`${p.product}|${p.pack}`} className="hover:bg-muted/50">
                          <td className="py-2 pr-3 font-medium text-on-surface">{p.product}</td>
                          <td className="py-2 pr-3 text-on-surface-variant">{p.pack || '—'}</td>
                          <td className="py-2 pr-3 text-right font-mono tabular-nums font-semibold text-on-surface">{safeMoney(p.quantity)}{p.unit ? ` ${/\d/.test(p.unit) ? p.unit.split(' ').pop() : p.unit}` : ''}</td>
                          <td className="py-2 text-right font-mono tabular-nums text-on-surface-variant" title={[...p.orders].join(', ')}>{p.orders.size}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1150px] text-left text-body-md border-collapse" aria-busy={isLoading}>
              <thead className="sticky top-0 z-10">
                <tr className="border-b border-border bg-muted text-label-sm font-semibold uppercase tracking-wider text-on-surface-variant">
                  {!readOnly && <th scope="col" className="py-2.5 pl-4 pr-2 w-10">
                    <input
                      type="checkbox"
                      aria-label="Select all dispatch records on this page"
                      checked={selectedRows.size === paginatedRecords.length && paginatedRecords.length > 0}
                      onChange={toggleSelectAll}
                      className="rounded border-outline-variant accent-primary h-3.5 w-3.5"
                    />
                  </th>}
                  <th scope="col" className="py-2.5 px-3">Order No.</th>
                  <th scope="col" className="py-2.5 px-3">Due Date</th>
                  <th scope="col" className="py-2.5 px-3">Dispatched Date</th>
                  <th scope="col" className="py-2.5 px-3">Client</th>
                  <th scope="col" className="py-2.5 px-3">Destination</th>
                  <th scope="col" className="py-2.5 px-3">Hand-off / Transport</th>
                  <th scope="col" className="py-2.5 px-3">Stage</th>
                  <th scope="col" className="py-2.5 px-3">DC / Challan No.</th>
                  <th scope="col" className="py-2.5 px-3 text-center">Docs</th>
                  <th scope="col" className="py-2.5 pr-4 pl-2 text-right min-w-[170px]">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border text-on-surface-variant">
                {isError && !isLoading ? (
                  <tr>
                    <td colSpan={readOnly ? 9 : 11} className="px-4 py-10 text-center">
                      <p className="text-body-md font-medium text-destructive">{getApiErrorMessage(error, 'The dispatch queue could not be loaded.')}</p>
                      <p className="mt-1 text-body-sm text-on-surface-variant">Nothing has been lost — the queue reloads on its own. Check the connection, or open an order directly from its number.</p>
                    </td>
                  </tr>
                ) : isLoading ? Array.from({ length: 8 }).map((_, i) => (
                  <tr key={`skeleton-${i}`}>
                    <td colSpan={readOnly ? 9 : 11} className="py-2.5 pl-4 pr-4">
                      <div className="flex items-center gap-4">
                        <Skeleton className="h-3.5 w-28" />
                        <Skeleton className="h-3.5 w-16" />
                        <Skeleton className="h-3.5 flex-1" />
                        <Skeleton className="h-5 w-28 rounded-full" />
                        <Skeleton className="h-3.5 w-24" />
                      </div>
                    </td>
                  </tr>
                )) : paginatedRecords.length === 0 ? (
                  <tr>
                    <td colSpan={readOnly ? 9 : 11} className="px-4 py-10 text-center">
                      <p className="text-body-md font-medium text-on-surface">Nothing waiting under these filters</p>
                      <p className="mt-1 text-body-sm text-on-surface-variant">
                        Orders arrive here the moment their pipeline record reaches Order Confirmed. Clear the stage and date filters, or raise a challan from a confirmed order.
                      </p>
                    </td>
                  </tr>
                ) : (
                  paginatedRecords.map((r) => {
                    const isChecked = selectedRows.has(r._id);
                    const isRowSelected = selectedRecord?._id === r._id || selectedRecord?.orderNumber === r.orderNumber;

                    return (
                      <tr
                        key={r._id}
                        onClick={() => setSelectedRecordId(r.orderNumber || r._id)}
                        className={cn(
                          'cursor-pointer transition hover:bg-slate-50/70',
                          isRowSelected ? 'bg-indigo-50/40' : (isChecked ? 'bg-indigo-50/50' : '')
                        )}
                      >
                        {!readOnly && <td className="py-2.5 pl-4 pr-2" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => toggleSelectRow(r._id)}
                            className="rounded border-slate-300 accent-indigo-600 h-3.5 w-3.5"
                          />
                        </td>}
                        <td className="py-2.5 px-3">
                          <Link
                            to={`/dispatch/orders/${r._id}`}
                            onClick={(e) => e.stopPropagation()}
                            className="font-mono font-medium text-on-surface underline-offset-2 hover:text-primary hover:underline"
                          >
                            {r.orderNumber || r._id.slice(-6)}
                          </Link>
                          <div className="mt-0.5 font-mono text-body-xs tabular-nums text-on-surface-subtle">
                            {safeDate(r.confirmedAt || r.date)}
                          </div>
                          {(r.stage === 'dispatched' || r.stage === 'delivered' || r.dispatchedAt || r.dispatchHandoff?.dispatchedAt) && (
                            <div className="mt-0.5 flex items-center gap-1 text-body-xs font-medium text-success-text">
                              <Truck className="h-3 w-3" aria-hidden />
                              <span>Dispatched</span>
                              <span className="font-mono tabular-nums">{safeDate(r.dispatchedAt || r.dispatchHandoff?.dispatchedAt || r.date)}</span>
                            </div>
                          )}
                        </td>
                        <td className="py-2.5 px-3">
                          <div className="font-mono tabular-nums text-on-surface">
                            {safeDate(r.requiredDispatchDate || r.requiredDeliveryDate, '-')}
                          </div>
                          {r.isOverdue && (
                            <div className="mt-0.5 text-label-caps text-danger-text">Overdue</div>
                          )}
                        </td>
                        <td className="py-2.5 px-3 whitespace-nowrap">
                          {r.dispatchedAt || r.dispatchHandoff?.dispatchedAt ? (
                            <div>
                              <div className="font-mono tabular-nums text-body-md text-success-text">
                                {safeDate(r.dispatchedAt || r.dispatchHandoff?.dispatchedAt)}
                              </div>
                              <div className="font-mono text-body-xs tabular-nums text-on-surface-subtle">
                                {safeTime(r.dispatchedAt || r.dispatchHandoff?.dispatchedAt)}
                              </div>
                            </div>
                          ) : r.stage === 'dispatched' || r.stage === 'delivered' ? (
                            <div className="font-mono tabular-nums text-body-md text-on-surface-variant">
                              {safeDate(r.updatedAt || r.date)}
                            </div>
                          ) : (
                            <span className="text-on-surface-subtle">—</span>
                          )}
                        </td>
                        <td className="py-2.5 px-3">
                          <div className="font-medium text-on-surface">{r.customerName || '—'}</div>
                          {r.customerPhone && (
                            <div className="mt-0.5 font-mono text-body-xs tabular-nums text-on-surface-subtle">{r.customerPhone}</div>
                          )}
                        </td>
                        <td className="py-2.5 px-3 text-on-surface-variant">
                          {r.shippingDestination || '—'}
                        </td>
                        <td className="py-2.5 px-3">
                          <div className="space-y-1">
                            <div className="text-body-sm font-medium text-on-surface">
                              {r.transportName && r.transportName !== '—' ? r.transportName : (r.transportMode ? r.transportMode.replace(/_/g, ' ') : '—')}
                            </div>
                            {r.vehicleNo && (
                              <div className="inline-flex items-center gap-1 rounded-card-sm bg-surface-variant px-1.5 py-0.5 font-mono text-body-xs text-on-surface-variant">
                                <Truck className="h-3 w-3" aria-hidden />
                                <span className="uppercase">{r.vehicleNo}</span>
                              </div>
                            )}
                            {(canMarkDispatch(user) || canSetTransportAmount(user)) && (
                              <div>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setFillTransportFor(r);
                                  }}
                                  title="View or edit transport & tracking details"
                                  className="inline-flex items-center gap-1 rounded-lg border border-input bg-surface px-2 py-0.5 text-body-xs font-medium text-on-surface transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                >
                                  Transport details
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="py-2.5 px-3">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <StageBadge stage={r.stage} />
                            {r.reversalRequest?.status === 'pending' && (
                              <span
                                title={`Reversal requested by ${r.reversalRequest.requestedByName || 'User'}: ${r.reversalRequest.reason || ''}`}
                                className="inline-flex items-center gap-1 rounded-full border border-danger/25 bg-danger/10 px-2 py-0.5 text-label-caps text-danger-text"
                              >
                                <RotateCcw className="h-3 w-3" aria-hidden /> Reversal Requested
                              </span>
                            )}
                            <StockShortBadge shortages={r.shortages} />
                            {/* Click the badge to fill what is missing (client, 2026-10-07). */}
                            {(r.dispatchBlockers || []).map((x) => (
                              x.code === 'LOCAL_TRANSPORT_REQUIRED' && (canMarkDispatch(user) || canSetTransportAmount(user)) ? (
                                <button
                                  key={x.code}
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); setFillTransportFor(r); }}
                                  title="Click to fill the local transport details"
                                  className="inline-flex items-center gap-1 rounded-full border border-warning/25 bg-warning/10 px-2 py-0.5 text-label-caps text-warning-text transition-colors hover:bg-warning/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                >
                                  Needs: {x.label} <span aria-hidden="true">→ Fill</span>
                                </button>
                              ) : (
                                <span key={x.code} title="Needed before Mark Dispatched" className="inline-flex items-center rounded-full border border-warning/25 bg-warning/10 px-2 py-0.5 text-label-caps text-warning-text">Needs: {x.label}</span>
                              )
                            ))}
                            {r.creditCleared && (
                              <span
                                title={r.creditClearanceNote ? `Cleared on credit: ${r.creditClearanceNote}` : 'Cleared for dispatch under credit'}
                                className="inline-flex items-center gap-1 rounded-full border border-success/25 bg-success/10 px-2 py-0.5 text-label-caps text-success-text"
                              >
                                <Check className="h-3 w-3" aria-hidden /> Credit cleared
                              </span>
                            )}
                            {!r.creditCleared && (r.paymentStatus === 'partial' || (r.amountPaid > 0 && r.amountDue > 0)) && (
                              <span
                                title={`Partial Payment: ₹${safeMoney(r.amountPaid)} paid of ₹${safeMoney(r.grandTotal)}. Shortfall due: ₹${safeMoney(r.amountDue)}`}
                                className="inline-flex items-center gap-1 rounded-full border border-warning/25 bg-warning/10 px-2 py-0.5 text-label-caps text-warning-text"
                              >
                                Partial <span className="font-mono tabular-nums">₹{safeMoney(r.amountPaid)} / ₹{safeMoney(r.grandTotal)}</span>
                              </span>
                            )}
                            {!r.creditCleared && r.paymentStatus !== 'paid' && !(r.amountDue !== undefined && r.amountDue <= 0 && r.grandTotal > 0) && (r.paymentArrangement === 'Credit' || r.paymentDetails?.paymentTerms?.startsWith('credit')) && (
                              <span
                                title="Order under credit arrangement"
                                className="inline-flex items-center gap-1 rounded-full border border-info/25 bg-info/10 px-2 py-0.5 text-label-caps text-info-text"
                              >
                                Credit {r.paymentDetails?.creditDays ? `(${r.paymentDetails.creditDays}d)` : ''}
                              </span>
                            )}
                            {r.podPending && (
                              <span title="Upload the POD once delivered — needed before Mark Delivered" className="inline-flex items-center rounded-full border border-info/25 bg-info/10 px-2 py-0.5 text-label-caps text-info-text">POD pending</span>
                            )}
                          </div>
                        </td>
                        <td className="py-2.5 px-3">
                          {r.documentType === 'sample_request' ? (
                            r.dcNumber ? (
                              <a
                                href={`/api/documents/sample-invoice/${r.sampleInvoiceId || r._id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="font-semibold text-indigo-600 hover:text-indigo-800 hover:underline inline-flex items-center gap-1"
                                title="View Sample Invoice"
                              >
                                <span>{r.dcNumber}</span>
                                <ExternalLink className="h-3 w-3 opacity-70" />
                              </a>
                            ) : (
                              <span className="text-slate-300">—</span>
                            )
                          ) : r.dcNumber ? (
                            r.activeChallanId ? (
                              <a
                                href={`/api/documents/challan/${r.activeChallanId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="font-semibold text-indigo-600 hover:text-indigo-800 hover:underline inline-flex items-center gap-1"
                                title="View Delivery Challan"
                              >
                                <span>{r.dcNumber}</span>
                                <ExternalLink className="h-3 w-3 opacity-70" />
                              </a>
                            ) : (
                              <span className="font-semibold text-indigo-600">
                                {r.dcNumber}
                              </span>
                            )
                          ) : r.dcDraftNumber ? (
                            r.activeChallanId ? (
                              <a
                                href={`/api/documents/challan/${r.activeChallanId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="text-[11px] text-warning-text hover:text-warning-text hover:underline font-medium italic inline-flex items-center gap-1"
                                title="View Draft Delivery Challan"
                              >
                                <span>{r.dcDraftNumber}</span>
                                <ExternalLink className="h-2.5 w-2.5 opacity-70" />
                              </a>
                            ) : (
                              <span className="text-[11px] text-warning-text font-medium italic">
                                {r.dcDraftNumber}
                              </span>
                            )
                          ) : (
                            <span className="text-slate-300">—</span>
                          )}
                        </td>
                        <td className="py-2.5 px-3 text-center">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedRecordId(r._id);
                              setActiveTab('documents');
                            }}
                            className="hover:scale-105 transition cursor-pointer"
                            title="View, upload or delete dispatch documents (LR / POD)"
                          >
                            <DocsBadge record={r} count={r.docsCount || 0} total={3} />
                          </button>
                        </td>
                        <td className="py-2.5 pr-4 pl-2 text-right relative" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-1.5">
                            {r.creditCleared && (
                              <span
                                title={r.creditClearanceNote ? `Cleared on credit: ${r.creditClearanceNote}` : 'Cleared for dispatch under credit'}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-success/10 text-success-text text-[11px] font-bold border border-success/25 whitespace-nowrap"
                              >
                                ✓ Cleared
                              </span>
                            )}
                            {canClearCredit && r.stage === 'awaiting_dispatch' && !r.creditCleared && (r.amountDue > 0 || isCreditHandoff(r.dispatchHandoff) || String(r.paymentArrangement || '').toLowerCase().startsWith('credit')) && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const note = window.prompt(`Clear order ${r.orderNumber} for dispatch under credit? Enter approval note:`, 'Approved on credit');
                                  if (note !== null) {
                                    clearCreditDispatchMutation.mutate({ orderId: r._id, note });
                                  }
                                }}
                                disabled={clearCreditDispatchMutation.isPending}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-xs font-semibold border border-indigo-200 transition whitespace-nowrap cursor-pointer"
                                title="Approve order for dispatch under credit"
                              >
                                <span>Clear on Credit</span>
                              </button>
                            )}
                            {canMarkDispatched && r.stage !== 'dispatched' && r.stage !== 'delivered' && (
                              <button
                                onClick={() => startDispatch(r)}
                                disabled={markDispatchedMutation.isPending}
                                className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-lg bg-primary px-3 py-1.5 text-body-sm font-semibold text-on-primary transition-colors hover:bg-primary-dim focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
                                title="Mark Order as Dispatched"
                              >
                                <Truck className="h-3.5 w-3.5" />
                                <span>Mark Dispatched</span>
                              </button>
                            )}

                            {canMarkDispatched && r.stage === 'dispatched' && (
                              <button
                                onClick={() => markDeliveredMutation.mutate({ orderId: r._id, isSample: r.documentType === 'sample_request' })}
                                disabled={markDeliveredMutation.isPending}
                                className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-lg border border-input bg-surface px-3 py-1.5 text-body-sm font-semibold text-on-surface transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
                                title="Mark as Delivered"
                              >
                                <Check className="h-3.5 w-3.5" />
                                <span>Mark Delivered</span>
                              </button>
                            )}

                            {r.stage === 'delivered' && (
                              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-success/10 text-success-text text-xs font-semibold border border-success/25 whitespace-nowrap">
                                <Check className="h-3 w-3" /> Delivered
                              </span>
                            )}

                            <button
                              onClick={() => setActionMenuOpenId(actionMenuOpenId === r._id ? null : r._id)}
                              className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition cursor-pointer"
                              title="More options"
                            >
                              <MoreVertical className="h-4 w-4" />
                            </button>
                          </div>

                          {actionMenuOpenId === r._id && (
                            <div className="absolute right-4 top-10 z-30 w-52 rounded-xl border border-slate-200 bg-white p-1.5 shadow-xl text-left">
                              <Link
                                to={`/dispatch/orders/${r._id}`}
                                className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium text-slate-700 hover:bg-slate-100"
                              >
                                <Eye className="h-3.5 w-3.5 text-slate-400" />
                                <span>View Picking Sheet</span>
                              </Link>
                              <button
                                onClick={() => {
                                  setActionMenuOpenId(null);
                                  setSelectedRecordId(r._id);
                                  setActiveTab('documents');
                                }}
                                className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium text-slate-700 hover:bg-slate-100 cursor-pointer"
                              >
                                <File className="h-3.5 w-3.5 text-slate-400" />
                                <span>Manage Docs (LR/POD)</span>
                              </button>
                              {!r.dcNumber && r.documentType !== 'sample_request' && (
                                <button
                                  onClick={() => {
                                    setActionMenuOpenId(null);
                                    createChallanMutation.mutate(r._id);
                                  }}
                                  className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium text-indigo-600 hover:bg-indigo-50 cursor-pointer"
                                >
                                  <FileText className="h-3.5 w-3.5 text-indigo-500" />
                                  <span>Issue Challan</span>
                                </button>
                              )}
                              {canMarkDispatched && r.stage !== 'dispatched' && r.stage !== 'delivered' && (
                                <button
                                  onClick={() => {
                                    setActionMenuOpenId(null);
                                    startDispatch(r);
                                  }}
                                  className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium text-success-text hover:bg-success/10 cursor-pointer"
                                >
                                  <Truck className="h-3.5 w-3.5 text-success-text" />
                                  <span>Mark Dispatched</span>
                                </button>
                              )}
                              {canMarkDispatched && r.stage === 'dispatched' && (
                                <button
                                  onClick={() => {
                                    setActionMenuOpenId(null);
                                    markDeliveredMutation.mutate({ orderId: r._id, isSample: r.documentType === 'sample_request' });
                                  }}
                                  className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium text-success-text hover:bg-success/10 cursor-pointer"
                                >
                                  <Check className="h-3.5 w-3.5 text-success-text" />
                                  <span>Mark Delivered</span>
                                </button>
                              )}
                              {/* Reversal Option with Admin Approval */}
                              {(r.stage === 'dispatched' || r.stage === 'delivered') && (
                                r.reversalRequest?.status === 'pending' ? (
                                  canReverseDispatch(user) ? (
                                    <>
                                      <button
                                        onClick={() => {
                                          setActionMenuOpenId(null);
                                          if (window.confirm(`Approve dispatch reversal for ${r.orderNumber || 'this order'}? This will restore inventory stock.`)) {
                                            reverseDispatchMutation.mutate({ orderId: r._id, isSample: r.documentType === 'sample_request' });
                                          }
                                        }}
                                        className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold text-danger-text bg-danger/10 hover:bg-danger/10 cursor-pointer"
                                      >
                                        <RotateCcw className="h-3.5 w-3.5 text-danger-text" />
                                        <span>Approve Reversal</span>
                                      </button>
                                      <button
                                        onClick={() => {
                                          setActionMenuOpenId(null);
                                          const reason = window.prompt(`Reject reversal request for ${r.orderNumber || 'this order'}? Enter reason (optional):`);
                                          if (reason !== null) {
                                            rejectReversalMutation.mutate({ orderId: r._id, reason });
                                          }
                                        }}
                                        className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium text-slate-600 hover:bg-slate-100 cursor-pointer"
                                      >
                                        <X className="h-3.5 w-3.5 text-slate-500" />
                                        <span>Reject Reversal</span>
                                      </button>
                                    </>
                                  ) : (
                                    <div className="px-3 py-1.5 text-[11px] font-semibold text-warning-text bg-warning/10 rounded-lg">
                                      Reversal pending Admin approval
                                    </div>
                                  )
                                ) : canReverseDispatch(user) ? (
                                  <button
                                    onClick={() => {
                                      setActionMenuOpenId(null);
                                      if (window.confirm(`Are you sure you want to REVERSE dispatch for ${r.orderNumber || 'this order'}? This will restore inventory stock movement.`)) {
                                        reverseDispatchMutation.mutate({ orderId: r._id, isSample: r.documentType === 'sample_request' });
                                      }
                                    }}
                                    className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium text-danger-text hover:bg-danger/10 cursor-pointer"
                                  >
                                    <RotateCcw className="h-3.5 w-3.5 text-danger-text" />
                                    <span>Reverse Dispatch</span>
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => {
                                      setActionMenuOpenId(null);
                                      const reason = window.prompt(`Request dispatch reversal for ${r.orderNumber || 'this order'}? Please enter reason for Admin approval:`);
                                      if (reason && reason.trim()) {
                                        requestReversalMutation.mutate({ orderId: r._id, reason: reason.trim() });
                                      }
                                    }}
                                    className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium text-warning-text hover:bg-warning/10 cursor-pointer"
                                  >
                                    <RotateCcw className="h-3.5 w-3.5 text-warning-text" />
                                    <span>Request Reversal</span>
                                  </button>
                                )
                              )}
                            </div>
                          )}

                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Footer */}
          <div className="px-5 py-2.5 border-t border-slate-100 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
            <div>
              Showing {filteredRecords.length === 0 ? 0 : (page - 1) * pageSize + 1} to{' '}
              {Math.min(page * pageSize, filteredRecords.length)} of {filteredRecords.length} records
            </div>
            <div className="flex flex-wrap items-center gap-1">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="px-2.5 py-1 rounded-lg border border-slate-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50"
              >
                &lt;
              </button>
              {Array.from({ length: totalPages }).map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setPage(idx + 1)}
                  className={cn(
                    'px-3 py-1 rounded-lg font-semibold transition',
                    page === idx + 1
                      ? 'bg-indigo-600 text-white shadow-sm'
                      : 'border border-slate-200 text-slate-700 hover:bg-slate-50'
                  )}
                >
                  {idx + 1}
                </button>
              ))}
              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="px-2.5 py-1 rounded-lg border border-slate-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50"
              >
                &gt;
              </button>
            </div>
          </div>
        </div>

        {/* ── Slide-Out Record Details Side Drawer ── */}
        {selectedRecord && (
          <>
          {/* Overlay drawer: the dispatch table keeps its full width underneath */}
          <div className="fixed inset-0 z-40 bg-black/20" onClick={() => setSelectedRecordId(null)} aria-hidden="true" />
          <div role="dialog" aria-modal="true" aria-label="Dispatch record details" className="fixed inset-y-0 right-0 z-50 w-full max-w-[480px] border-l border-outline bg-surface shadow-2xl overflow-y-auto flex flex-col">
            {/* Drawer Header */}
            <div className="p-4 border-b border-slate-100 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600 border border-indigo-100/60">
                  <Truck className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <div className="font-bold text-sm text-slate-900 leading-tight">
                    {selectedRecord.orderNumber || selectedRecord._id.slice(-6)}
                  </div>
                  <div className="text-[11px] text-slate-400 truncate">
                    {selectedRecord.customerName} · {safeDate(selectedRecord.confirmedAt || selectedRecord.date)}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <StageBadge stage={selectedRecord.stage} />
                <StockShortBadge shortages={selectedRecord.shortages} />
                <button
                  onClick={() => setSelectedRecordId(null)}
                  className="p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Drawer Tabs */}
            <div className="flex border-b border-slate-100 px-4 bg-slate-50/50">
              <button
                onClick={() => setActiveTab('overview')}
                className={cn(
                  'px-3 py-2.5 text-xs font-semibold border-b-2 transition -mb-px',
                  activeTab === 'overview'
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-slate-500 hover:text-slate-800'
                )}
              >
                Overview
              </button>
              <button
                onClick={() => setActiveTab('documents')}
                className={cn(
                  'px-3 py-2.5 text-xs font-semibold border-b-2 transition -mb-px',
                  activeTab === 'documents'
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-slate-500 hover:text-slate-800'
                )}
              >
                Documents
              </button>
              <button
                onClick={() => setActiveTab('timeline')}
                className={cn(
                  'px-3 py-2.5 text-xs font-semibold border-b-2 transition -mb-px',
                  activeTab === 'timeline'
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-slate-500 hover:text-slate-800'
                )}
              >
                Timeline
              </button>
            </div>

            {/* Drawer Body */}
            <div className="p-4 space-y-5 overflow-y-auto max-h-[calc(100vh-280px)]">
              {selectedRecord.reversalRequest?.status === 'pending' && (
                <div className="rounded-xl border border-danger/25 bg-danger/10 p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5 text-xs font-bold text-danger-text">
                      <RotateCcw className="h-4 w-4 text-danger-text animate-spin" />
                      Dispatch Reversal Requested
                    </div>
                    <span className="text-[10px] font-semibold uppercase px-2 py-0.5 rounded bg-danger/15 text-danger-text">Pending Approval</span>
                  </div>
                  <div className="text-xs text-danger-text">
                    <span className="font-semibold">Reason:</span> {selectedRecord.reversalRequest.reason}
                  </div>
                  <div className="text-[11px] text-danger-text">
                    Requested by {selectedRecord.reversalRequest.requestedByName || 'User'}
                    {selectedRecord.reversalRequest.requestedAt && ` on ${safeDate(selectedRecord.reversalRequest.requestedAt)}`}
                  </div>
                  {canReverseDispatch(user) && (
                    <div className="flex items-center gap-2 pt-1">
                      <button
                        type="button"
                        onClick={() => {
                          if (window.confirm(`Approve reversal for ${selectedRecord.orderNumber}? This will reverse dispatch and restore inventory stock.`)) {
                            reverseDispatchMutation.mutate({ orderId: selectedRecord._id, isSample: selectedRecord.documentType === 'sample_request' });
                          }
                        }}
                        disabled={reverseDispatchMutation.isPending}
                        className="px-3 py-1.5 rounded-lg bg-destructive hover:bg-destructive/90 text-on-primary text-xs font-bold shadow-xs transition"
                      >
                        Approve & Reverse Dispatch
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const reason = window.prompt('Enter reason for rejecting this reversal request (optional):');
                          if (reason !== null) {
                            rejectReversalMutation.mutate({ orderId: selectedRecord._id, reason });
                          }
                        }}
                        disabled={rejectReversalMutation.isPending}
                        className="px-3 py-1.5 rounded-lg bg-white border border-danger/25 hover:bg-danger/10 text-danger-text text-xs font-semibold transition"
                      >
                        Reject Request
                      </button>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'overview' && (
                <>
                  {/* Order Details Grid */}
                  <div className="grid grid-cols-2 gap-y-3 gap-x-4 text-xs border-b border-slate-100 pb-4">
                    <div>
                      <div className="text-[11px] text-slate-400 font-medium">Order Number</div>
                      <div className="font-bold text-slate-800 mt-0.5">{selectedRecord.orderNumber}</div>
                    </div>
                    <div>
                      <div className="text-[11px] text-slate-400 font-medium">Order Date</div>
                      <div className="font-semibold text-slate-800 mt-0.5">
                        {safeDate(selectedRecord.confirmedAt || selectedRecord.date)}
                      </div>
                    </div>
                    <div>
                      <div className="text-[11px] text-slate-400 font-medium">Client</div>
                      <div className="font-semibold text-slate-800 mt-0.5 flex items-center gap-1">
                        <span>{selectedRecord.customerName}</span>
                      </div>
                      {selectedRecord.customerPhone && (
                        <div className="text-[11px] text-slate-500 flex items-center gap-1 mt-0.5">
                          <Phone className="h-3 w-3 text-indigo-500" />
                          <span>{selectedRecord.customerPhone}</span>
                        </div>
                      )}
                    </div>
                    <div>
                      <div className="text-[11px] text-slate-400 font-medium">Items</div>
                      <div className="font-semibold text-slate-800 mt-0.5">
                        {selectedRecord.itemsSummary || `${selectedRecord.lineCount} Items`}
                      </div>
                    </div>
                    <div>
                      <div className="text-[11px] text-slate-400 font-medium">Destination</div>
                      <div className="font-semibold text-slate-800 mt-0.5">
                        {selectedRecord.shippingDestination || '—'}
                      </div>
                    </div>
                    <div>
                      <div className="text-[11px] text-slate-400 font-medium">Current Stage</div>
                      <div className="mt-0.5 flex items-center gap-1.5 flex-wrap">
                        <StageBadge stage={selectedRecord.stage} />
                        <StockShortBadge shortages={selectedRecord.shortages} />
                      </div>
                      <div className="text-[10px] text-slate-400 mt-0.5">
                        {selectedRecord.stage === 'dispatched'
                          ? 'Goods departed warehouse'
                          : selectedRecord.stage === 'delivered'
                          ? 'Delivered to recipient'
                          : 'Confirmed, not yet fulfilled'}
                      </div>
                    </div>
                    <div>
                      <div className="text-[11px] text-slate-400 font-medium">Handoff / Transport</div>
                      <div className="font-semibold text-slate-800 mt-0.5">
                        {selectedRecord.transportName || '—'}
                      </div>
                      {selectedRecord.vehicleNo && (
                        <div className="text-[11px] text-slate-500 font-mono mt-0.5">{selectedRecord.vehicleNo}</div>
                      )}
                    </div>
                    <div>
                      <div className="text-[11px] text-slate-400 font-medium">DC / Challan No.</div>
                      <div className="font-semibold text-indigo-600 mt-0.5 flex items-center gap-1.5">
                        <span>{selectedRecord.dcNumber || selectedRecord.dcDraftNumber || '—'}</span>
                        {selectedRecord.documentType === 'sample_request' ? (
                          <a
                            href={`/api/documents/sample-invoice/${selectedRecord.sampleInvoiceId || selectedRecord._id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-[11px] font-medium text-indigo-600 hover:text-indigo-800 underline bg-indigo-50 px-1.5 py-0.5 rounded"
                            title="Open Sample Invoice"
                          >
                            <span>View</span>
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        ) : selectedRecord.activeChallanId && (
                          <a
                            href={`/api/documents/challan/${selectedRecord.activeChallanId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-[11px] font-medium text-indigo-600 hover:text-indigo-800 underline bg-indigo-50 px-1.5 py-0.5 rounded"
                            title="Open Delivery Challan"
                          >
                            <span>View</span>
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Items & HSN Breakdown Section */}
                  {selectedRecord.lines && selectedRecord.lines.length > 0 && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <h3 className="text-xs font-bold text-slate-800">
                          Items &amp; HSN ({selectedRecord.lines.length})
                        </h3>
                        <span className="text-[11px] text-slate-400">
                          {selectedRecord.itemsSummary}
                        </span>
                      </div>
                      <div className="space-y-1.5 max-h-52 overflow-y-auto pr-1">
                        {selectedRecord.lines.map((item, idx) => (
                          <div
                            key={idx}
                            className="p-2.5 rounded-xl border border-slate-200/70 bg-slate-50/60 flex items-center justify-between gap-2 text-xs"
                          >
                            <div className="min-w-0 flex-1">
                              <div className="font-semibold text-slate-800 truncate" title={item.description}>
                                {item.description}
                              </div>
                              <div className="text-[11px] text-slate-500 flex items-center gap-2 mt-0.5">
                                <span>
                                  Qty: <b className="text-slate-700">{item.quantity}</b> {item.unit || 'Nos'}
                                </span>
                                {item.packSize && (
                                  <span className="text-slate-400">· {item.packSize}</span>
                                )}
                              </div>
                            </div>
                            <span className="shrink-0 px-2 py-0.5 rounded-md bg-white border border-slate-200 font-mono text-[11px] font-bold text-indigo-700 shadow-sm" title="HSN/SAC Code">
                              HSN: {item.hsnSac || '27101980'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Documents Section */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <h3 className="text-xs font-bold text-slate-800">
                          Dispatch Documents
                        </h3>
                        <DocsBadge record={selectedRecord} count={selectedRecord.docsCount || 0} total={3} />
                      </div>
                      <Link
                        to={`/dispatch/orders/${selectedRecord._id}`}
                        className="text-[11px] font-semibold text-indigo-600 hover:underline flex items-center gap-0.5"
                      >
                        Picking Sheet <ArrowRight className="h-3 w-3" />
                      </Link>
                    </div>

                    {/* The REAL, live balance — straight from the Accounts ledger
                        (same amountDue a payment recorded below immediately
                        updates), not the one-time snapshot taken at hand-off.
                        Shown here so it's impossible to miss right where a
                        payment proof is about to be attached. */}
                    {selectedRecord.documentType !== 'sample_request' && (
                      <div className="rounded-xl border border-slate-200 bg-white p-3.5 space-y-3 shadow-2xs">
                        <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                          <div>
                            <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">
                              Payment & Financial Status
                            </span>
                            <div className="text-xs font-semibold text-slate-700 mt-0.5">
                              Terms: {selectedRecord.paymentArrangement || selectedRecord.paymentDetails?.paymentTermsLabel || (selectedRecord.paymentStatus === 'paid' ? 'Paid in Full' : 'Standard')}
                            </div>
                          </div>
                          <span className={cn(
                            'shrink-0 px-2.5 py-1 rounded-full text-[11px] font-bold',
                            selectedRecord.paymentStatus === 'paid' ? 'bg-success/10 text-success-text' : 'bg-warning/10 text-warning-text',
                          )}>
                            {selectedRecord.paymentStatus === 'paid' ? 'Fully paid' : `Due ₹${safeMoney(selectedRecord.amountDue)}`}
                          </span>
                        </div>

                        {/* Partial Payment Breakdown */}
                        <div className="space-y-1.5">
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-slate-500">Total Order Value:</span>
                            <span className="font-bold text-slate-800">₹{safeMoney(selectedRecord.grandTotal)}</span>
                          </div>
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-slate-500">Advance / Received:</span>
                            <span className="font-bold text-success-text">₹{safeMoney(selectedRecord.amountPaid)}</span>
                          </div>
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-slate-500">Pending Shortfall / Due:</span>
                            <span className="font-bold text-danger-text font-mono">₹{safeMoney(selectedRecord.amountDue)}</span>
                          </div>

                          {/* Progress bar */}
                          {Number(selectedRecord.grandTotal || 0) > 0 && (
                            <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden mt-1">
                              <div
                                className={cn('h-full transition-all', Number(selectedRecord.amountPaid || 0) >= Number(selectedRecord.grandTotal || 0) ? 'bg-success' : 'bg-warning')}
                                style={{ width: `${Math.min(100, Math.round((Number(selectedRecord.amountPaid || 0) / Number(selectedRecord.grandTotal || 1)) * 100))}%` }}
                              />
                            </div>
                          )}
                        </div>

                        {/* Credit Clearance Controls */}
                        {selectedRecord.creditCleared ? (
                          <div className="rounded-lg border border-success/25 bg-success/10 p-2.5 flex items-start justify-between gap-2">
                            <div>
                              <div className="text-xs font-bold text-success-text flex items-center gap-1">
                                <span>✓ Cleared for Dispatch on Credit</span>
                              </div>
                              <p className="text-[11px] text-success-text mt-0.5">
                                {selectedRecord.creditClearanceNote || 'Approved for dispatch under credit.'}
                              </p>
                              {selectedRecord.creditClearedAt && (
                                <p className="text-[10px] text-success-text mt-0.5">
                                  Approved: {safeDate(selectedRecord.creditClearedAt)}
                                </p>
                              )}
                            </div>
                            {canClearCredit && (
                              <button
                                type="button"
                                onClick={() => {
                                  if (window.confirm('Revoke credit clearance for this order?')) {
                                    revokeCreditClearanceMutation.mutate(selectedRecord._id);
                                  }
                                }}
                                disabled={revokeCreditClearanceMutation.isPending}
                                className="text-[10px] font-semibold text-danger-text hover:underline shrink-0"
                              >
                                Revoke
                              </button>
                            )}
                          </div>
                        ) : (
                          canClearCredit && selectedRecord.stage === 'awaiting_dispatch' && (
                            <div className="pt-1 border-t border-slate-100 flex items-center justify-between gap-2">
                              <span className="text-[11px] text-slate-500">
                                Dispatch held for payment?
                              </span>
                              <button
                                type="button"
                                onClick={() => {
                                  const note = window.prompt(`Approve order ${selectedRecord.orderNumber} for dispatch under credit? Enter approval note:`, 'Approved on credit');
                                  if (note !== null) {
                                    clearCreditDispatchMutation.mutate({ orderId: selectedRecord._id, note });
                                  }
                                }}
                                disabled={clearCreditDispatchMutation.isPending}
                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold shadow-xs transition"
                              >
                                <span>Clear on Credit</span>
                              </button>
                            </div>
                          )
                        )}
                      </div>
                    )}

                    {/* Sales and Dispatch can both attach documents here — a customer
                        paying in installments means a payment proof can legitimately
                        arrive well after this order was already handed off. */}
                    <div className="rounded-xl border border-slate-200/80 bg-slate-50/50 p-3 space-y-2.5">
                      <div className="flex items-center justify-between gap-2">
                        <label className="text-[11px] font-semibold text-slate-600">Upload Document</label>
                        <select
                          value={docUploadKind}
                          onChange={(e) => {
                            const next = e.target.value as typeof docUploadKind;
                            setDocUploadKind(next);
                            // The amount being paid is, more often than not, whatever
                            // is still outstanding — pre-filling it saves re-typing
                            // the number that's already shown just above, and it's
                            // still freely editable for a partial instalment.
                            if (next === 'payment_proof' && !docPaymentAmount.trim() && selectedRecord.amountDue > 0) {
                              setDocPaymentAmount(String(selectedRecord.amountDue));
                            }
                          }}
                          className="text-xs font-medium text-slate-700 bg-white border border-slate-200 rounded-lg px-2.5 py-1"
                        >
                          <option value="lr">LR / e-way bill</option>
                          <option value="payment_proof">Payment proof</option>
                          <option value="pod">Delivery proof / POD</option>
                        </select>
                      </div>

                      {docUploadKind === 'payment_proof' && (
                        <>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={docPaymentAmount}
                            onChange={(e) => setDocPaymentAmount(e.target.value)}
                            placeholder="Amount received (optional)"
                            className="w-full text-xs px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white placeholder:text-slate-400"
                          />
                          {/* Same 4 labels as the hand-off form's own "Payment
                              arrangement" (DocumentActions.tsx) — minus Credit,
                              which is decided once at the first hand-off and means
                              no money changes hands, so it has nothing to do here. */}
                          <select
                            value={docPaymentMethod}
                            onChange={(e) => setDocPaymentMethod(e.target.value as any)}
                            disabled={!docPaymentAmount.trim()}
                            className="w-full text-xs font-medium text-slate-700 bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 disabled:opacity-50"
                          >
                            <option value="Cash">Cash (Paid in Advance)</option>
                            <option value="UPI">UPI (Paid in Advance)</option>
                            <option value="IMPS / NEFT">Online / Bank Transfer (IMPS / NEFT - Paid Advance)</option>
                          </select>

                          {/* Cash has no bank/UPI receipt to photograph — the same
                              rule the hand-off form itself already applies (proof
                              is required for UPI/IMPS-NEFT, optional for Cash). */}
                          {docPaymentAmount.trim() && docPaymentMethod === 'Cash' && (
                            <button
                              type="button"
                              onClick={handleRecordCashPayment}
                              disabled={isUploading}
                              className="w-full text-xs font-semibold text-on-primary bg-success-text hover:bg-success-text/90 rounded-lg py-1.5 disabled:opacity-50 transition"
                            >
                              {isUploading ? 'Recording…' : `Record ₹${safeMoney(docPaymentAmount)} cash payment — no receipt needed`}
                            </button>
                          )}
                          {docPaymentAmount.trim() && docPaymentMethod !== 'Cash' && (
                            <div className="text-[10px] text-slate-500">
                              Proof is required for UPI / Bank Transfer — attach the receipt below to record this amount.
                            </div>
                          )}
                        </>
                      )}

                      <input
                        type="text"
                        value={docUploadNote}
                        onChange={(e) => setDocUploadNote(e.target.value)}
                        placeholder="Optional note (e.g. 2nd installment, ₹50,000)"
                        maxLength={500}
                        className="w-full text-xs px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white placeholder:text-slate-400"
                      />

                      {/* Drop Area */}
                      <div
                        onClick={() => { if (!isUploading) fileInputRef.current?.click(); }}
                        className={`border-2 border-dashed rounded-xl p-4 text-center bg-white transition ${
                          isUploading ? 'border-indigo-300 opacity-60 cursor-not-allowed' : 'border-slate-200 hover:border-indigo-400 cursor-pointer'
                        }`}
                      >
                        <UploadCloud className="h-7 w-7 text-indigo-500 mx-auto" />
                        <div className="mt-1 text-xs font-semibold text-slate-800">
                          {isUploading ? 'Uploading document…' : (
                            <>Drop files here, or <span className="text-indigo-600">choose files</span></>
                          )}
                        </div>
                        <div className="text-[10px] text-slate-400 mt-0.5">PDF or images, up to 25 MB each.</div>
                        <input
                          ref={fileInputRef}
                          type="file"
                          multiple
                          accept="image/png,image/jpeg,image/webp,application/pdf"
                          onChange={(e) => handleFileUpload(e.target.files)}
                          className="hidden"
                          disabled={isUploading}
                        />
                      </div>
                    </div>

                    {/* Required Documents Checklist (Item 52) */}
                    {(() => {
                      const docs = selectedRecord.docs || [];
                      const hasInv = Boolean((selectedRecord.invoiceNumbers && selectedRecord.invoiceNumbers.length > 0) || docs.some((d) => d.kind === 'invoice'));
                      const hasLr = docs.some((d) => d.kind === 'lr');
                      const hasPay = Boolean(docs.some((d) => d.kind === 'payment_proof' || d.kind === 'pod') || selectedRecord.creditCleared || selectedRecord.paymentStatus === 'paid');
                      const allClear = hasInv && hasLr && hasPay;

                      return (
                        <div className="space-y-2.5 rounded-card border border-border bg-card p-3 shadow-xs">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-label-sm uppercase tracking-wider text-on-surface-subtle">
                              Dispatch documents
                            </span>
                            {allClear ? (
                              <Badge tone="success"><CheckCircle2 className="h-3 w-3" aria-hidden /> All clear</Badge>
                            ) : (
                              <Badge tone="warning">Action required</Badge>
                            )}
                          </div>
                          <div className="grid grid-cols-3 gap-1.5 text-center text-body-sm">
                            {([['Invoice', hasInv], ['LR / e-way', hasLr], ['Payment / POD', hasPay]] as const).map(([label, present]) => (
                              <div
                                key={label}
                                className={cn(
                                  'flex flex-col items-center gap-1 rounded-lg border p-2',
                                  present ? 'border-success/25 bg-success/10 text-success-text' : 'border-danger/25 bg-danger/10 text-danger-text',
                                )}
                              >
                                <span className="text-label-sm uppercase tracking-wider">{label}</span>
                                <span className="inline-flex items-center gap-1 font-medium">
                                  {present ? <Check className="h-3 w-3" aria-hidden /> : <AlertTriangle className="h-3 w-3" aria-hidden />}
                                  {present ? 'Attached' : 'Missing'}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })()}

                    {/* Attached Documents List */}
                    <div className="space-y-2">
                      <div className="text-[11px] font-bold text-slate-600 uppercase tracking-wide">
                        Attached Documents ({selectedRecord.docs?.length || 0})
                      </div>

                      {!selectedRecord.docs || selectedRecord.docs.length === 0 ? (
                        <div className="p-4 rounded-xl border border-slate-100 bg-slate-50/60 text-center text-slate-400 text-xs">
                          <File className="h-5 w-5 mx-auto mb-1 opacity-50" />
                          No documents attached yet.
                          <div className="text-[11px] text-slate-400 mt-0.5">
                            Add LR / e-way bill, delivery proof and payment proof here.
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-1.5">
                          {selectedRecord.docs.filter((doc) => doc.kind !== 'invoice').map((doc) => (
                            <div
                              key={doc.id || doc.url}
                              className="p-2.5 rounded-xl border border-slate-200/80 bg-white flex items-center justify-between gap-2 text-xs"
                            >
                              <div className="flex items-center gap-2 min-w-0">
                                <File className="h-4 w-4 text-indigo-500 shrink-0" />
                                <div className="min-w-0">
                                  <div className="font-semibold text-slate-800 truncate">{doc.name}</div>
                                  <div className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold">
                                    {doc.kind === 'lr'
                                      ? 'LR / E-way'
                                      : doc.kind === 'payment_proof'
                                      ? 'Payment Proof'
                                      : 'POD'}
                                  </div>
                                  {doc.note && (
                                    <div className="text-[10px] text-slate-500 mt-0.5 truncate normal-case font-normal">{doc.note}</div>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-1 shrink-0">
                                <a
                                  href={doc.url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="p-1 rounded-md text-slate-400 hover:text-indigo-600 transition"
                                  title="View document"
                                >
                                  <ExternalLink className="h-4 w-4" />
                                </a>
                                <button
                                  type="button"
                                  disabled={deleteDocMutation.isPending}
                                  onClick={() => {
                                    if (window.confirm(`Are you sure you want to delete "${doc.name}"? You can then upload a replacement.`)) {
                                      deleteDocMutation.mutate({
                                        orderId: selectedRecord._id,
                                        url: doc.url,
                                        docId: doc.id,
                                        name: doc.name,
                                        kind: doc.kind,
                                      });
                                    }
                                  }}
                                  className="p-1 rounded-md text-slate-400 hover:text-danger-text hover:bg-danger/10 transition cursor-pointer"
                                  title="Delete document to replace it"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </div>

                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

              {activeTab === 'documents' && (
                <div className="space-y-3">
                  <div className="text-xs font-bold text-slate-800">Consignment Documents</div>
                  {selectedRecord.docs?.length ? (
                    <div className="space-y-2">
                      {selectedRecord.docs.map((d) => (
                        <div key={d.id} className="p-3 rounded-xl border border-slate-200 bg-white flex items-center justify-between">
                          <div className="flex items-center gap-2.5">
                            <File className="h-5 w-5 text-indigo-500" />
                            <div>
                              <div className="font-semibold text-xs text-slate-800">{d.name}</div>
                              <div className="text-[11px] text-slate-400 capitalize">{d.kind.replace('_', ' ')}</div>
                              {d.note && <div className="text-[11px] text-slate-500 mt-0.5">{d.note}</div>}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <a
                              href={d.url}
                              target="_blank"
                              rel="noreferrer"
                              className="px-2.5 py-1 text-xs font-semibold rounded-lg bg-indigo-50 text-indigo-600 hover:bg-indigo-100"
                            >
                              View
                            </a>
                            <button
                              type="button"
                              disabled={deleteDocMutation.isPending}
                              onClick={() => {
                                if (window.confirm(`Are you sure you want to delete "${d.name}"? You can then upload a replacement.`)) {
                                  deleteDocMutation.mutate({
                                    orderId: selectedRecord._id,
                                    url: d.url,
                                    docId: d.id,
                                    name: d.name,
                                    kind: d.kind,
                                  });
                                }
                              }}
                              className="p-1.5 rounded-lg text-slate-400 hover:text-danger-text hover:bg-danger/10 transition cursor-pointer"
                              title="Delete document to replace it"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>

                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-400 text-xs">No documents attached yet.</div>
                  )}
                </div>
              )}

              {activeTab === 'timeline' && (
                <div className="space-y-4 relative pl-4 border-l-2 border-indigo-100 ml-2 py-1 text-xs">
                  <div className="relative">
                    <div className="absolute -left-[21px] top-1 h-3 w-3 rounded-full bg-success ring-4 ring-white" />
                    <div className="font-bold text-slate-900">Order Confirmed</div>
                    <div className="text-slate-500 text-[11px]">
                      {safeDate(selectedRecord.confirmedAt || selectedRecord.date)}
                    </div>
                  </div>

                  <div className="relative">
                    <div className={cn('absolute -left-[21px] top-1 h-3 w-3 rounded-full ring-4 ring-white', selectedRecord.dispatchHandoff ? 'bg-indigo-500' : 'bg-slate-300')} />
                    <div className="font-bold text-slate-900">Sales Handoff Created</div>
                    <div className="text-slate-500 text-[11px]">
                      {selectedRecord.dispatchHandoff?.handedOffAt
                        ? safeDate(selectedRecord.dispatchHandoff.handedOffAt)
                        : 'Completed'}
                    </div>
                  </div>

                  <div className="relative">
                    <div className={cn('absolute -left-[21px] top-1 h-3 w-3 rounded-full ring-4 ring-white', selectedRecord.dcNumber ? 'bg-indigo-500' : 'bg-slate-300')} />
                    <div className="font-bold text-slate-900">Delivery Challan Issued</div>
                    <div className="text-slate-500 text-[11px]">
                      {selectedRecord.dcNumber ? selectedRecord.dcNumber : 'Pending creation'}
                    </div>
                  </div>

                  <div className="relative">
                    <div className={cn('absolute -left-[21px] top-1 h-3 w-3 rounded-full ring-4 ring-white', selectedRecord.stage === 'dispatched' || selectedRecord.stage === 'delivered' ? 'bg-success' : 'bg-slate-300')} />
                    <div className="font-bold text-slate-900">Goods Dispatched</div>
                    <div className="text-slate-500 text-[11px]">
                      {selectedRecord.stage === 'dispatched' || selectedRecord.stage === 'delivered'
                        ? 'Consignment picked up and in transit'
                        : 'Awaiting dispatch'}
                    </div>
                  </div>

                  <div className="relative">
                    <div className={cn('absolute -left-[21px] top-1 h-3 w-3 rounded-full ring-4 ring-white', selectedRecord.stage === 'delivered' ? 'bg-success' : 'bg-slate-300')} />
                    <div className="font-bold text-slate-900">Delivery Confirmed</div>
                    <div className="text-slate-500 text-[11px]">
                      {selectedRecord.stage === 'delivered' ? 'Goods received by customer' : 'Pending'}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Fulfilment actions */}
            {!readOnly && <div className="p-4 border-t border-slate-100 bg-slate-50/50 flex items-center justify-between gap-3">
              {selectedRecord.documentType !== 'sample_request' && (
                <button
                  onClick={() => createChallanMutation.mutate(selectedRecord._id)}
                  disabled={createChallanMutation.isPending || !!selectedRecord.dcNumber}
                  className="flex-1 py-2.5 px-3 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-xs font-semibold text-slate-700 shadow-sm transition disabled:opacity-50 flex items-center justify-center gap-1.5 cursor-pointer"
                >
                  <FileText className="h-4 w-4 text-indigo-500" />
                  <span>{selectedRecord.dcNumber ? 'Challan Created' : 'Create Challan'}</span>
                </button>
              )}

              {canMarkDispatched && (
                <button
                  onClick={() => {
                    if (selectedRecord.stage === 'dispatched') {
                      markDeliveredMutation.mutate({ orderId: selectedRecord._id, isSample: selectedRecord.documentType === 'sample_request' });
                    } else if (selectedRecord.stage !== 'delivered') {
                      startDispatch(selectedRecord);
                    }
                  }}
                  disabled={selectedRecord.stage === 'delivered' || markDispatchedMutation.isPending || markDeliveredMutation.isPending}
                  className={cn(
                    "flex-1 py-2.5 px-3 rounded-xl text-white text-xs font-semibold shadow-sm transition disabled:opacity-50 flex items-center justify-center gap-1.5 cursor-pointer",
                    selectedRecord.stage === 'dispatched' ? "bg-success hover:bg-success" : "bg-success hover:bg-success"
                  )}
                >
                  {selectedRecord.stage === 'dispatched' ? <Check className="h-4 w-4" /> : <Truck className="h-4 w-4" />}
                  <span>
                    {selectedRecord.stage === 'delivered'
                      ? 'Delivered'
                      : selectedRecord.stage === 'dispatched'
                      ? 'Mark as Delivered'
                      : 'Mark as Dispatched'}
                  </span>
                </button>
              )}

              {canReverseDispatch(user) && (selectedRecord.stage === 'dispatched' || selectedRecord.stage === 'delivered') && (
                <button
                  onClick={() => {
                    if (window.confirm(`Are you sure you want to REVERSE dispatch for ${selectedRecord.orderNumber || 'this order'}? This requires Admin approval and will restore inventory stock.`)) {
                      reverseDispatchMutation.mutate({
                        orderId: selectedRecord._id,
                        isSample: selectedRecord.documentType === 'sample_request',
                      });
                    }
                  }}
                  disabled={reverseDispatchMutation.isPending}
                  className="py-2.5 px-3 rounded-xl border border-danger/25 bg-danger/10 hover:bg-danger/10 text-xs font-semibold text-danger-text shadow-sm transition disabled:opacity-50 flex items-center justify-center gap-1.5 cursor-pointer shrink-0"
                  title="Reverse dispatch (Admin only)"
                >
                  <RotateCcw className="h-4 w-4 text-danger-text" />
                  <span>Reverse Dispatch</span>
                </button>
              )}
            </div>}

          </div>
          </>
        )}
      </div>

      {/* ── Create Dispatch Order Modal ── */}
      {isCreateModalOpen && (
        <CreateDispatchOrderModal
          onClose={() => setIsCreateModalOpen(false)}
          onSuccess={() => {
            setIsCreateModalOpen(false);
            qc.invalidateQueries({ queryKey: ['dispatch', 'queue'] });
          }}
        />
      )}

      {/* ── Sticky Bulk Actions for Combined Transport & Split Amount ── */}
      {selectedRows.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 rounded-2xl bg-slate-900/95 px-5 py-3 text-white shadow-2xl backdrop-blur border border-slate-700 animate-in fade-in slide-in-from-bottom-4">
          <div className="flex items-center gap-2">
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary font-mono text-body-xs tabular-nums font-bold text-on-primary">
              {selectedRows.size}
            </span>
            <span className="text-xs font-semibold text-slate-200">
              {selectedRows.size === 1 ? '1 order selected' : `${selectedRows.size} orders selected`}
            </span>
          </div>
          <div className="h-4 w-px bg-slate-700" />
          {(canMarkDispatch(user) || canSetTransportAmount(user)) && (
            <button
              type="button"
              onClick={() => {
                const selectedList = paginatedRecords.filter((r) => selectedRows.has(r._id));
                if (selectedList.length === 1) {
                  setFillTransportFor(selectedList[0]);
                } else if (selectedList.length > 1) {
                  setBulkTransportRecords(selectedList);
                }
              }}
              className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg bg-primary px-3.5 py-1.5 text-body-sm font-semibold text-on-primary shadow-surface-2 transition-colors hover:bg-primary-dim focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Truck className="h-3.5 w-3.5" />
              <span>{selectedRows.size > 1 ? `Combined Transport & Split Amount (${selectedRows.size} Orders)` : 'Transport Details'}</span>
            </button>
          )}
          <button
            type="button"
            onClick={() => setSelectedRows(new Set())}
            className="rounded-xl px-2.5 py-1.5 text-xs font-medium text-slate-400 hover:text-white transition cursor-pointer"
          >
            Clear
          </button>
        </div>
      )}
    </div>
  );
}

// ── Subcomponent: Stage Badge ──
function StageBadge({ stage }: { stage: DispatchStage }) {
  // Status chips read their colour from the -text tokens: the bright fills fail
  // contrast as text. The dot keeps the fill.
  const TONE: Record<string, { chip: string; dot: string }> = {
    awaiting_dispatch: { chip: 'border-info/25 bg-info/10 text-info-text', dot: 'bg-info' },
    draft_challan: { chip: 'border-warning/25 bg-warning/10 text-warning-text', dot: 'bg-warning' },
    dispatched: { chip: 'border-success/25 bg-success/10 text-success-text', dot: 'bg-success' },
    delivered: { chip: 'border-success/25 bg-success/10 text-success-text', dot: 'bg-success' },
    stock_short: { chip: 'border-danger/25 bg-danger/10 text-danger-text', dot: 'bg-danger' },
  };
  const tone = TONE[stage];
  if (!tone) {
    return <Badge tone="neutral">{String(stage).replaceAll('_', ' ')}</Badge>;
  }
  return (
    <span className={cn('inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-0.5 text-label-caps', tone.chip)}>
      <span className={cn('h-1.5 w-1.5 rounded-full', tone.dot)} aria-hidden />
      {stageLabel(stage)}
    </span>
  );
}

// ── Subcomponent: Stock Short warning ──
// A shortage no longer changes the stage badge (it stays Awaiting Dispatch) —
// this is the alert instead: a lightweight, read-only indicator, separate
// from the manual stockFlag escalation workflow on the order detail page.
function StockShortBadge({ shortages }: { shortages: { description: string; short: number }[] }) {
  if (!shortages.length) return null;
  const title = shortages.map((s) => `${s.description}: short ${s.short}`).join('\n');
  return (
    <span
      title={title}
      className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-danger/25 bg-danger/10 px-2 py-0.5 text-label-caps text-danger-text"
    >
      <AlertTriangle className="h-3 w-3" aria-hidden />
      Stock Short
    </span>
  );
}

function stageLabel(stage: DispatchStage) {
  switch (stage) {
    case 'awaiting_dispatch': return 'Awaiting Dispatch';
    case 'draft_challan': return 'Draft Challan';
    case 'dispatched': return 'Dispatched';
    case 'delivered': return 'Delivered';
    case 'stock_short': return 'Stock Short';
    default: return stage;
  }
}

// ── Subcomponent: Docs Badge (Item 52: Missing document names / All Clear) ──
function DocsBadge({
  record,
  count,
  total = 3,
}: {
  record?: DispatchRecord;
  count?: number;
  total?: number;
}) {
  const docs = record?.docs || [];
  const hasInvoice = Boolean(
    (record?.invoiceNumbers && record.invoiceNumbers.length > 0) ||
    docs.some((d) => d.kind === 'invoice')
  );
  const hasLr = docs.some((d) => d.kind === 'lr');
  const hasPaymentOrPod = Boolean(
    docs.some((d) => d.kind === 'payment_proof' || d.kind === 'pod') ||
    record?.creditCleared ||
    record?.paymentStatus === 'paid'
  );

  const missing: string[] = [];
  if (!hasInvoice) missing.push('Invoice');
  if (!hasLr) missing.push('LR/e-way');
  if (!hasPaymentOrPod) missing.push('Payment/POD');

  const actualCount = count ?? (3 - missing.length);
  const isComplete = missing.length === 0 || actualCount >= total;

  if (isComplete) {
    return (
      <span
        title="All 3 dispatch documents present (Invoice, LR / e-way bill, Payment proof / POD)"
        className="inline-flex items-center gap-1 whitespace-nowrap rounded-card-sm border border-success/25 bg-success/10 px-2 py-0.5 text-label-sm font-semibold text-success-text"
      >
        <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
        <span>All Clear</span>
      </span>
    );
  }

  return (
    <div className="flex flex-col items-center gap-0.5" title={`Missing: ${missing.join(', ')}`}>
      <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-card-sm border border-warning/25 bg-warning/10 px-1.5 py-0.5 text-label-sm font-semibold text-warning-text">
        <FileText className="h-3 w-3" aria-hidden />
        <span className="font-mono tabular-nums">{actualCount}/{total}</span> Docs
      </span>
      <span className="max-w-[130px] truncate text-body-xs font-medium text-danger-text">
        Missing: {missing.join(', ')}
      </span>
    </div>
  );
}

// ── Modal: Create Dispatch Order ──
function CreateDispatchOrderModal({
  onClose,
  onSuccess,
}: {
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [selectedOrderId, setSelectedOrderId] = useState('');
  const [transportMode, setTransportMode] = useState<'in_house_driver' | 'third_party_courier'>('third_party_courier');
  const [courierName, setCourierName] = useState('');
  const [customCourierName, setCustomCourierName] = useState('');
  const [vehicleNo, setVehicleNo] = useState('');
  const [driverName, setDriverName] = useState('');
  const [paymentArrangement, setPaymentArrangement] = useState('Credit');
  const [creditAmount, setCreditAmount] = useState('');
  const [requiredDispatchDate, setRequiredDispatchDate] = useState(
    new Date(Date.now() + 86400000 * 2).toISOString().slice(0, 10)
  );
  const [note, setNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch confirmed orders available for handoff
  const { data: availableOrders = [] } = useQuery({
    queryKey: ['sales-orders', 'confirmed-for-dispatch'],
    queryFn: async () => {
      const res = await api.get<{ data: any[] }>('/api/sales-ops/orders?documentType=sales_order&status=confirmed');
      return res.data?.data || [];
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedOrderId) {
      toast.error('Please select a confirmed Sales Order.');
      return;
    }
    if (transportMode === 'third_party_courier') {
      if (!courierName.trim() || (courierName === 'Other' && !customCourierName.trim())) {
        toast.error('Please specify courier or transporter name.');
        return;
      }
    }
    if (!requiredDispatchDate) {
      toast.error('Please specify a dispatch due date.');
      return;
    }

    setIsSubmitting(true);
    try {
      await api.post(`/api/documents/transitions/sales_order/${selectedOrderId}/to/delivery_challan`, {
        dispatchAction: 'handoff',
        createDeliveryChallan: true,
        transportMode,
        courierName: transportMode === 'third_party_courier' ? (courierName === 'Other' ? customCourierName.trim() : courierName.trim()) : '',
        driverName: transportMode === 'in_house_driver' ? driverName.trim() : '',
        vehicleNo: vehicleNo.trim(),
        paymentArrangement,
        creditAmount: Number(creditAmount) || 0,
        requiredDispatchDate,
        note: note.trim(),
      });
      toast.success('Dispatch Order and Delivery Challan generated successfully!');
      onSuccess();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to create dispatch order.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden flex flex-col max-h-[90vh]">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
              <Truck className="h-5 w-5" />
            </div>
            <h2 className="text-base font-bold text-slate-900">Create Dispatch Order</h2>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg text-slate-400 hover:text-slate-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4 overflow-y-auto flex-1 text-xs">
          <div>
            <label className="font-semibold text-slate-700 block mb-1">Select Confirmed Sales Order *</label>
            <select
              value={selectedOrderId}
              onChange={(e) => setSelectedOrderId(e.target.value)}
              className="w-full rounded-xl border border-slate-200 p-2.5 text-xs bg-white focus:ring-2 focus:ring-indigo-500/20"
              required
            >
              <option value="">-- Choose Sales Order --</option>
              {availableOrders.map((o: any) => (
                <option key={o._id} value={o._id}>
                  {o.orderNumber || o._id.slice(-6)} - {o.customerName} (₹{safeMoney(o.grandTotal)})
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="font-semibold text-slate-700 block mb-1">Transport Mode</label>
              <select
                value={transportMode}
                onChange={(e) => setTransportMode(e.target.value as any)}
                className="w-full rounded-xl border border-slate-200 p-2.5 text-xs bg-white"
              >
                <option value="third_party_courier">Third-Party Courier</option>
                <option value="in_house_driver">In-House Driver</option>
              </select>
            </div>
            <div>
              <label className="font-semibold text-slate-700 block mb-1">Dispatch Due Date *</label>
              <input
                type="date"
                value={requiredDispatchDate}
                onChange={(e) => setRequiredDispatchDate(e.target.value)}
                className="w-full rounded-xl border border-slate-200 p-2.5 text-xs bg-white"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="font-semibold text-slate-700 block mb-1">
                {transportMode === 'in_house_driver' ? 'Driver Name' : 'Transporter / Courier Name *'}
              </label>
              {transportMode === 'in_house_driver' ? (
                <input
                  type="text"
                  value={driverName}
                  onChange={(e) => setDriverName(e.target.value)}
                  placeholder="e.g. Ramesh"
                  className="w-full rounded-xl border border-slate-200 p-2.5 text-xs bg-white"
                />
              ) : (
                <>
                  <select
                    value={courierName}
                    onChange={(e) => setCourierName(e.target.value)}
                    className="w-full rounded-xl border border-slate-200 p-2.5 text-xs bg-white"
                    required
                  >
                    <option value="" disabled>Select courier name</option>
                    {COURIER_PRESETS.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                  {courierName === 'Other' && (
                    <div className="mt-2">
                      <input
                        type="text"
                        value={customCourierName}
                        onChange={(e) => setCustomCourierName(e.target.value)}
                        placeholder="Enter custom courier name"
                        className="w-full rounded-xl border border-slate-200 p-2.5 text-xs bg-white"
                        required
                      />
                    </div>
                  )}
                </>
              )}
            </div>
            <div>
              <label className="font-semibold text-slate-700 block mb-1">Vehicle No. / LR</label>
              <input
                type="text"
                value={vehicleNo}
                onChange={(e) => setVehicleNo(e.target.value)}
                placeholder="e.g. DL01AB1234"
                className="w-full rounded-xl border border-slate-200 p-2.5 text-xs"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="font-semibold text-slate-700 block mb-1">Payment Arrangement</label>
              <select
                value={paymentArrangement}
                onChange={(e) => setPaymentArrangement(e.target.value)}
                className="w-full rounded-xl border border-slate-200 p-2.5 text-xs bg-white"
              >
                <option value="Credit">Credit (Pay Later / Net 30)</option>
                <option value="Cash">Cash (Paid in Advance)</option>
                <option value="UPI">UPI (Paid in Advance)</option>
                <option value="IMPS / NEFT">IMPS / NEFT (Paid Advance)</option>
              </select>
            </div>
            <div>
              <label className="font-semibold text-slate-700 block mb-1">Credit Amount (₹)</label>
              <input
                type="number"
                value={creditAmount}
                onChange={(e) => setCreditAmount(e.target.value)}
                placeholder="0"
                className="w-full rounded-xl border border-slate-200 p-2.5 text-xs"
              />
            </div>
          </div>

          <div>
            <label className="font-semibold text-slate-700 block mb-1">Note for Dispatch</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="Special loading or handling instructions..."
              className="w-full rounded-xl border border-slate-200 p-2.5 text-xs"
            />
          </div>

          <div className="pt-2 flex justify-end gap-2 border-t border-slate-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 font-semibold"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold disabled:opacity-50 transition"
            >
              {isSubmitting ? 'Creating...' : 'Hand Off & Create Dispatch Order'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default DispatchAndChallansPage;
